/**
 * Утилиты для работы с API-
 */

//const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Получение токена из localStorage
const getToken = () => localStorage.getItem('access_token');

export const isAbortError = (error) => {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || '');
  return (
    name === 'AbortError'
    || error.code === 20
    || /aborted|AbortError/i.test(message)
  );
};

export const parseCreditsError = (errorText) => {
  try {
    // Пытаемся распарсить JSON из ответа
    let detail = errorText;
    
    // Если это JSON строка, пытаемся её распарсить
    if (errorText.startsWith('{')) {
      try {
        const parsed = JSON.parse(errorText);
        detail = parsed.detail || parsed.message || errorText;
      } catch (e) {
        // JSON не распарсился, используем как есть
      }
    }
    
    // Проверяем, содержит ли сообщение информацию о кредитах или подписке
    const isCreditsOrSubscriptionError = 
      (detail.includes('кредит') || detail.includes('Кредит')) ||
      detail.toLowerCase().includes('токен') ||
      detail.includes('Триальный') ||
      detail.includes('триальный') ||
      detail.includes('триал') ||
      (detail.includes('Подписк') || detail.includes('подписк')) ||
      (detail.includes('402') || detail.includes('Payment')) ||
      detail.includes('лимит запросов') ||
      detail.includes('Лимит запросов') ||
      detail.includes('лимит исчерпан');
    
    if (isCreditsOrSubscriptionError) {
      // Извлекаем числа из сообщения о кредитах
      const requiredMatch = detail.match(/Требуется:\s*([\d.,]+)/);
      const availableMatch = detail.match(/доступно:\s*([\d.,]+)/);
      
      if (requiredMatch && availableMatch) {
        const required = Math.ceil(parseFloat(requiredMatch[1].replace(',', '.')));
        const available = Math.floor(parseFloat(availableMatch[1].replace(',', '.')));
        if (detail.toLowerCase().includes('токен')) {
          return `Недостаточно токенов. Требуется: ${required}, доступно: ${available}`;
        }
        
        return `Недостаточно AI кредитов. Требуется: ${required}, доступно: ${available}`;
      }
      
      // Возвращаем оригинальное сообщение, очищенное от лишних символов
      return detail.replace(/(\d+)\.0+(?=\D|$)/g, '$1').replace(/(\d+\.\d*?)0+(?=\D|$)/g, '$1');
    }
    
    return null;
  } catch (e) {
    return null;
  }
};

// Вспомогательные функции для аутентификации
const AUTH_STORAGE_KEYS = [
  'accessToken',
  'refreshToken',
  'userRole',
  'userEmail',
  'adminPermissions',
  'hasAdminAccess',
  'isFullAdmin',
  'activeCompany',
  'isInCompanyMode',
  'isImpersonating',
  'impersonatorEmail',
  'impersonatorId'
];

const clearStoredAuthSession = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  window.dispatchEvent(new Event('authStatusChanged'));
};

const applyProfileToStorage = (profile) => {
  localStorage.setItem('userRole', profile?.role || 'user');
  localStorage.setItem('userEmail', profile?.email || '');
  localStorage.setItem(
    'adminPermissions',
    JSON.stringify(Array.isArray(profile?.admin_permissions) ? profile.admin_permissions : [])
  );
  localStorage.setItem('hasAdminAccess', profile?.has_admin_access ? 'true' : 'false');
  localStorage.setItem('isFullAdmin', profile?.is_full_admin ? 'true' : 'false');

  if (profile?.ad_company_locked || profile?.active_company_info) {
    if (profile?.active_company_info) {
      localStorage.setItem('activeCompany', JSON.stringify(profile.active_company_info));
    }
    localStorage.setItem('isInCompanyMode', 'true');
  } else if (profile?.active_company_info) {
    localStorage.setItem('activeCompany', JSON.stringify(profile.active_company_info));
    localStorage.setItem('isInCompanyMode', 'true');
  } else {
    localStorage.removeItem('activeCompany');
    localStorage.setItem('isInCompanyMode', 'false');
  }

  const impersonationInfo = profile?.impersonation_info;
  if (impersonationInfo?.is_active) {
    localStorage.setItem('isImpersonating', 'true');
    localStorage.setItem('impersonatorEmail', impersonationInfo.impersonator_email || '');
    localStorage.setItem('impersonatorId', impersonationInfo.impersonator_id || '');
  } else {
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('impersonatorEmail');
    localStorage.removeItem('impersonatorId');
  }

  window.dispatchEvent(new Event('authStatusChanged'));
};

const persistAuthSession = async (tokenResponse) => {
  if (!tokenResponse?.access_token) {
    return tokenResponse;
  }

  localStorage.setItem('accessToken', tokenResponse.access_token);
  localStorage.setItem('refreshToken', tokenResponse.refresh_token);

  try {
    const profile = await apiRequest('/auth/profile', { quietErrors: true });
    applyProfileToStorage(profile);
  } catch (error) {
    console.error('Ошибка получения профиля:', error);
    applyProfileToStorage({ role: 'user', email: '' });
  }

  return tokenResponse;
};

const getAuthHeaders = () => {
  // Проверяем оба варианта имени токена для совместимости
  const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const isAbortLikeError = (error) => {
  const name = error?.name || '';
  const message = error?.message || '';

  return (
    name === 'AbortError' ||
    name === 'TimeoutError' ||
    message.includes('AbortError') ||
    message.includes('TimeoutError') ||
    message.includes('signal is aborted') ||
    message.includes('aborted without reason') ||
    message.includes('The operation was aborted')
  );
};

const abortWithTimeoutReason = (controller, message) => {
  if (typeof DOMException === 'function') {
    controller.abort(new DOMException(message, 'AbortError'));
    return;
  }

  controller.abort();
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 10000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => abortWithTimeoutReason(controller, 'Таймаут проверки соединения'),
    timeoutMs
  );

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal
    });
  } finally {
    clearTimeout(timeoutId);
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    //const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (response.ok) {
      const tokenData = await response.json();
      localStorage.setItem('accessToken', tokenData.access_token);
      localStorage.setItem('refreshToken', tokenData.refresh_token);
      return tokenData.access_token;
    }
    
    // Очищаем токены если обновление не удалось
    clearStoredAuthSession();
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    
    return null;
  } catch (error) {
    console.error('Ошибка обновления токена:', error);
    return null;
  }
};

// Функция для быстрых API запросов с коротким таймаутом (например, проверка статуса)
const quickApiRequest = async (endpoint, options = {}, timeoutSeconds = 10) => {
  //const baseURL = process.env.REACT_APP_API_URL || '/api';
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`⏰ Таймаут для ${endpoint} после ${timeoutSeconds} секунд`);
      controller.abort();
    }, timeoutSeconds * 1000);

    const response = await fetch(`/api/${endpoint}`, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...getAuthHeaders(),
        ...options.headers
      }
    });

    clearTimeout(timeoutId);

    if (response.status === 401) {
      const newToken = await refreshAuthToken();
      if (newToken) {
        return await quickApiRequest(endpoint, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${newToken}`
          }
        }, timeoutSeconds);
      }
      throw new Error('Сессия истекла, требуется повторная авторизация');
    }

    if (!response.ok) {
      const errorText = await response.text();
      
      // Специальная обработка 402/429 ошибок (кредиты и лимит запросов)
      if (response.status === 402 || response.status === 429) {
        const creditsErrorMessage = parseCreditsError(errorText);
        if (creditsErrorMessage) {
          throw new Error(creditsErrorMessage);
        }
        if (response.status === 429) {
          throw new Error('Лимит запросов к модели исчерпан. Попробуйте позже или выберите другую модель.');
        }
        throw new Error('Недостаточно AI кредитов для выполнения операции. Пополните баланс или выберите другой тариф.');
      }
      
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    if (isAbortLikeError(error)) {
      throw new Error(`Таймаут запроса (${timeoutSeconds}с). Возможно, сервер не отвечает.`);
    }
    throw error;
  }
};

// Функция для загрузки файлов с увеличенным таймаутом
const uploadFileRequest = async (endpoint, formData, maxRetries = 1, timeoutMinutesOverride = null) => {
  //const baseURL = process.env.REACT_APP_API_URL || '/api';
  
  // Получаем размер файла для диагностики заранее
  const fileSize = formData.get('file')?.size || 0;
  const isRagDocumentUpload = endpoint === 'rag-v2/documents/upload';
  const isDesktopAgentRelease = endpoint === 'admin/desktop-agent/releases';
  const isDiskUpload = endpoint === 'disk/upload';
  const timeoutMinutes = timeoutMinutesOverride ?? (isDesktopAgentRelease
    ? 30
    : isDiskUpload
    ? 15
    : isRagDocumentUpload
    ? (fileSize > 100 * 1024 * 1024 ? 15 : fileSize > 50 * 1024 * 1024 ? 10 : 5)
    : (fileSize > 50 * 1024 * 1024 ? 5 : 2));
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Загрузка файла: ${endpoint} (попытка ${attempt}/${maxRetries})`);
      
      // Проверяем соединение с сервером перед загрузкой
      await testConnection({ includeOllama: false });
      console.log('Соединение с сервером проверено успешно');
      
      console.log(`📦 Размер файла: ${fileSize} байт (${(fileSize/1024/1024).toFixed(2)} MB)`);
      
      if (fileSize > 100 * 1024 * 1024) { // 100MB
        console.warn(`⚠️ БОЛЬШОЙ ФАЙЛ: ${fileSize} байт - увеличиваем таймаут`);
      }
      
      const controller = new AbortController();
             // Таймаут для загрузки файлов: загрузка должна быть быстрой, транскрибация идет в фоне
      const timeoutId = setTimeout(() => {
        console.log(`⏰ Таймаут загрузки файла после ${timeoutMinutes} минут`);
        console.log(`📝 Примечание: Это таймаут загрузки файла, не транскрибации. Транскрибация продолжается в фоне.`);
        abortWithTimeoutReason(controller, `Таймаут загрузки файла после ${timeoutMinutes} минут`);
      }, timeoutMinutes * 60 * 1000);

      console.log(`🚀 Отправляем файл к /api/${endpoint}`);
      
      const response = await fetch(`/api/${endpoint}`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'Connection': 'keep-alive',
          'Keep-Alive': 'timeout=3600, max=1000',
          // Заголовки для обхода WAF проверок для легитимных загрузок файлов
          'X-File-Upload': 'true',
          'X-Content-Safe': 'file-upload',
          ...getAuthHeaders()
        }
      });

      clearTimeout(timeoutId);
      console.log(`📡 Ответ получен: статус ${response.status}`);

      if (response.status === 401) {
        console.log('🔐 Токен истек, пытаемся обновить...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          console.log('✅ Токен обновлен, повторяем загрузку...');
          return await uploadFileRequest(endpoint, formData, maxRetries);
        }
        throw new Error('Сессия истекла, требуется повторная авторизация');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ HTTP ошибка ${response.status}: ${errorText}`);
        
        // Специальная обработка 402/429 ошибок (кредиты и лимит запросов)
        if (response.status === 402 || response.status === 429) {
          const creditsErrorMessage = parseCreditsError(errorText);
          if (creditsErrorMessage) {
            throw new Error(creditsErrorMessage);
          }
          if (response.status === 429) {
            throw new Error('Лимит запросов к модели исчерпан. Попробуйте позже или выберите другую модель.');
          }
          throw new Error('Недостаточно AI кредитов для выполнения операции. Пополните баланс или выберите другой тариф.');
        }
        
        // Специальная обработка 403 ошибки от WAF
        if (response.status === 403) {
          if (errorText.includes('nginx') || errorText.includes('Forbidden')) {
            throw new Error(
              `Загрузка заблокирована системой безопасности (WAF). ` +
              `Возможно, файл содержит подозрительные паттерны или превышен лимит запросов. ` +
              `Пожалуйста, попробуйте позже или обратитесь к администратору.`
            );
          }
        }
        
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      console.log(`✅ Загрузка завершена: ${endpoint}, статус: ${response.status} (попытка ${attempt})`);
      
      const data = await response.json();
      console.log(`📊 Данные получены: размер ответа ~${JSON.stringify(data).length} символов`);
      
      return data;

    } catch (error) {
      console.log(`❌ Upload file request error (попытка ${attempt}):`, {
        message: error.message,
        endpoint: endpoint,
        type: error.name
      });

      if (attempt === maxRetries) {
        console.error(`💥 Попытка загрузки неудачна для ${endpoint}`);
        
        // Специальное сообщение для таймаутов загрузки
        if (isAbortLikeError(error) || error.message.includes('Таймаут')) {
          throw new Error(`Загрузка файла заняла слишком много времени (${timeoutMinutes} мин). Возможно, файл слишком большой или медленное соединение. Попробуйте уменьшить размер файла или проверьте подключение к интернету.`);
        }
        
        throw new Error(`Проблема с загрузкой файла. Проверьте подключение к интернету и попробуйте снова. (Исходная ошибка: ${error.message})`);
      }

      // Определяем задержку между попытками
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') || 
          isAbortLikeError(error)) {
        const delay = attempt * 3000; // 3s, 6s для сетевых ошибок при загрузке файлов
        console.log(`🔄 Сетевая ошибка при загрузке, повторяем через ${delay/1000} секунд...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error; // Не повторяем для других типов ошибок
      }
    }
  }
};

// Функция для длительных операций (обработка больших текстов)
export const longRunningApiRequest = async (endpoint, options = {}, maxRetries = 3) => {
  //const baseURL = process.env.REACT_APP_API_URL || '/api';
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Запуск длительной операции: ${endpoint} (попытка ${attempt}/${maxRetries})`);
      
      // Проверяем соединение с сервером перед отправкой большого запроса
      await testConnection();
      console.log('Соединение с сервером проверено успешно');
      
      // Детальная диагностика размера запроса
      const bodySize = options.body ? new Blob([options.body]).size : 0;
      console.log(`📦 Размер тела запроса: ${bodySize} байт (${(bodySize/1024).toFixed(2)} KB)`);
      
      if (bodySize > 70000) {
        console.warn(`⚠️ БОЛЬШОЙ ЗАПРОС: ${bodySize} байт - потенциальная проблема с Nginx`);
      }
      
      const controller = new AbortController();
      // Специальные таймауты для разных операций  
      let timeoutMinutes = 120; // по умолчанию 120 минут
      
      if (endpoint.includes('/text/meeting-protocol')) {
        timeoutMinutes = 180; // 180 минут (3 часа) для протоколов с большими моделями
        console.log(`🚀 СПЕЦИАЛЬНЫЙ ТАЙМАУТ ДЛЯ ПРОТОКОЛА: ${timeoutMinutes} минут (gpt-oss:120b может требовать больше времени)`);
      }
      
      const timeoutId = setTimeout(() => {
        console.log(`⏰ Таймаут для ${endpoint} после ${timeoutMinutes} минут`);
        abortWithTimeoutReason(controller, `Таймаут операции после ${timeoutMinutes} минут`);
      }, timeoutMinutes * 60 * 1000);

      console.log(`🚀 Отправляем запрос к /api/${endpoint}`);
      
      const response = await fetch(`/api/${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Connection': 'keep-alive',
          'Keep-Alive': 'timeout=3600, max=1000',
          ...getAuthHeaders(),
          ...options.headers
        }
      });

      clearTimeout(timeoutId);
      console.log(`📡 Ответ получен: статус ${response.status}, Content-Type: ${response.headers.get('content-type')}`);

      if (response.status === 401) {
        console.log('🔐 Токен истек, пытаемся обновить...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          console.log('✅ Токен обновлен, повторяем запрос...');
          return await longRunningApiRequest(endpoint, {
            ...options,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${newToken}`
            }
          }, maxRetries);
        }
        throw new Error('Сессия истекла, требуется повторная авторизация');
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ HTTP ошибка ${response.status}: ${errorText}`);
        
        // Специальная обработка 402/429 ошибок (кредиты и лимит запросов)
        if (response.status === 402 || response.status === 429) {
          const creditsErrorMessage = parseCreditsError(errorText);
          if (creditsErrorMessage) {
            throw new Error(creditsErrorMessage);
          }
          if (response.status === 429) {
            throw new Error('Лимит запросов к модели исчерпан. Попробуйте позже или выберите другую модель.');
          }
          throw new Error('Недостаточно AI кредитов для выполнения операции. Пополните баланс или выберите другой тариф.');
        }
        
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      console.log(`✅ Операция завершена: ${endpoint}, статус: ${response.status} (попытка ${attempt})`);
      
      const data = await response.json();
      console.log(`📊 Данные получены: размер ответа ~${JSON.stringify(data).length} символов`);
      
      return data;

    } catch (error) {
      console.log(`❌ Long-running API request error (попытка ${attempt}):`, {
        message: error.message,
        endpoint: endpoint,
        type: error.name,
        stack: error.stack
      });

      if (attempt === maxRetries) {
        console.error(`💥 Все ${maxRetries} попытки неудачны для ${endpoint}`);
        throw new Error(`Проблема с сетевым соединением. Проверьте подключение к интернету и попробуйте снова. (Исходная ошибка: ${error.message})`);
      }

      // Определяем задержку между попытками
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') || 
          isAbortLikeError(error)) {
        const delay = attempt * 2000; // 2s, 4s для сетевых ошибок
        console.log(`🔄 Сетевая ошибка обнаружена, повторяем через ${delay/1000} секунд...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error; // Не повторяем для других типов ошибок
      }
    }
  }
};

const normalizeEndpoint = (endpoint = '') => String(endpoint).replace(/^\/+/, '');

// Глобальная защита от дублирующихся/слишком частых GET-запросов.
// Помогает, если в UI случайно стартует несколько одинаковых polling-циклов.
const throttledGetState = new Map();

const throttledGetRequest = async (endpoint, minIntervalMs = 0, options = {}) => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const now = Date.now();
  const currentState = throttledGetState.get(normalizedEndpoint) || {};
  const force = Boolean(options?.force);

  if (!force) {
    if (currentState.inFlightPromise) {
      return await currentState.inFlightPromise;
    }

    if (
      currentState.lastSuccessAt &&
      (now - currentState.lastSuccessAt) < minIntervalMs &&
      currentState.lastSuccessData !== undefined
    ) {
      return currentState.lastSuccessData;
    }
  }

  const requestPromise = apiRequest(endpoint, options)
    .then((data) => {
      const latest = throttledGetState.get(normalizedEndpoint) || {};
      throttledGetState.set(normalizedEndpoint, {
        ...latest,
        inFlightPromise: null,
        lastSuccessAt: Date.now(),
        lastSuccessData: data
      });
      return data;
    })
    .catch((error) => {
      const latest = throttledGetState.get(normalizedEndpoint) || {};
      throttledGetState.set(normalizedEndpoint, {
        ...latest,
        inFlightPromise: null
      });
      throw error;
    });

  throttledGetState.set(normalizedEndpoint, {
    ...currentState,
    inFlightPromise: requestPromise
  });

  return await requestPromise;
};

// Базовая функция для API запросов
const apiRequest = async (endpoint, options = {}) => {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  //const baseURL = process.env.REACT_APP_API_URL || '/api';
  const quietErrors = Boolean(options.quietErrors);
  
  if (!quietErrors) {
    console.log(`🌐 apiRequest: ${options.method || 'GET'} /api/${normalizedEndpoint}`, options.body ? { body: options.body } : '');
  }
  
  // Таймаут: по умолчанию 5 минут, можно передать кастомный через options.timeout
  const timeoutMs = options.timeout || 300000; // 5 минут = 300000 мс
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => abortWithTimeoutReason(controller, 'Таймаут API-запроса'),
    timeoutMs
  );
  
  const hasBody = options.body !== undefined;
  const isFormDataBody = hasBody && options.body instanceof FormData;
  const normalizedHeaders = { ...(options.headers || {}) };
  const hasExplicitContentType = Object.keys(normalizedHeaders).some(
    (headerName) => headerName.toLowerCase() === 'content-type'
  );

  // Для FormData нельзя вручную задавать Content-Type:
  // браузер сам добавляет multipart boundary.
  if (!isFormDataBody && !hasExplicitContentType) {
    normalizedHeaders['Content-Type'] = 'application/json';
  }

  const config = {
    headers: normalizedHeaders,
    signal: controller.signal,
    method: options.method || 'GET',
  };

  // Добавляем body только если он явно передан и не пустой
  if (options.body !== undefined) {
    config.body = options.body;
  }

  // Добавляем токен авторизации если он есть
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    if (!quietErrors) {
      console.log(`🚀 Fetching: /api/${normalizedEndpoint}`);
    }
    const response = await fetch(`/api/${normalizedEndpoint}`, config);
    if (!quietErrors) {
      console.log(`📡 Response status: ${response.status} for /api/${normalizedEndpoint}`);
    }
    
    // Очищаем таймаут при успешном ответе
    clearTimeout(timeoutId);
    
    // Если токен истек, пытаемся обновить его
    if (response.status === 401 && token) {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResponse = await fetch(`/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          });

          if (refreshResponse.ok) {
            const tokenData = await refreshResponse.json();
            localStorage.setItem('accessToken', tokenData.access_token);
            localStorage.setItem('refreshToken', tokenData.refresh_token);
            
            // Повторяем оригинальный запрос с новым токеном
            config.headers.Authorization = `Bearer ${tokenData.access_token}`;
            const retryResponse = await fetch(`/api/${normalizedEndpoint}`, config);
            
            if (retryResponse.ok) {
              return await retryResponse.json();
            }
          }
        }
        
        // Если обновление токена не удалось, очищаем токены и редиректим на логин
        clearStoredAuthSession();
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        throw new Error('Требуется повторная авторизация');
      } catch (refreshError) {
        clearStoredAuthSession();
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        throw new Error('Сессия истекла');
      }
    }

    if (!response.ok) {
      let errorPayload = { detail: 'Неизвестная ошибка' };
      try {
        // Клонируем response для возможности повторного чтения
        const clonedResponse = response.clone();
        errorPayload = await response.json();
      } catch (jsonError) {
        // Используем клонированный response для чтения текста
        try {
          const errorText = await response.clone().text();
          errorPayload = { detail: errorText || `HTTP error! status: ${response.status}` };
        } catch (textError) {
          errorPayload = { detail: `HTTP error! status: ${response.status}` };
        }
      }

      const formatErrorDetail = (detail) => {
        if (typeof detail === 'string') {
          return detail;
        }
        if (Array.isArray(detail)) {
          const validationMessages = detail
            .map((item) => {
              if (!item || typeof item !== 'object') return '';
              const path = Array.isArray(item.loc)
                ? item.loc.map((segment) => String(segment)).join(' -> ')
                : '';
              const rawMessage = item.msg ?? item.message ?? item.detail ?? '';
              const message =
                typeof rawMessage === 'string'
                  ? rawMessage
                  : (() => {
                      try {
                        return JSON.stringify(rawMessage);
                      } catch (_e) {
                        return String(rawMessage);
                      }
                    })();
              if (path && message) return `${path}: ${message}`;
              return message;
            })
            .filter(Boolean);

          if (validationMessages.length > 0) {
            return validationMessages.join('; ');
          }
        }
        if (detail && typeof detail === 'object') {
          if (typeof detail.message === 'string') {
            return detail.message;
          }
          try {
            return JSON.stringify(detail);
          } catch (_e) {
            return String(detail);
          }
        }
        return `HTTP error! status: ${response.status}`;
      };

      const rawDetail = errorPayload?.detail ?? errorPayload?.message ?? errorPayload;
      const detailMessage = formatErrorDetail(rawDetail);
      
      // Специальная обработка 402/429 ошибок (кредиты и лимит запросов)
      if (response.status === 402 || response.status === 429) {
        const creditsErrorMessage = parseCreditsError(JSON.stringify(errorPayload));
        if (creditsErrorMessage) {
          throw new Error(creditsErrorMessage);
        }
        if (response.status === 429) {
          throw new Error(detailMessage || 'Лимит запросов к модели исчерпан. Попробуйте позже или выберите другую модель.');
        }
        throw new Error('Недостаточно AI кредитов для выполнения операции. Пополните баланс или выберите другой тариф.');
      }
      
      if (typeof detailMessage === 'string' && detailMessage.includes('Граф построен, но не содержит данных')) {
        throw new Error('Граф построен, но не содержит данных для протокола. Проверьте содержание транскрибации.');
      }

      const apiError = new Error(detailMessage);
      apiError.status = response.status;
      apiError.endpoint = normalizedEndpoint;
      apiError.payload = errorPayload;
      throw apiError;
    }

    try {
      const data = await response.json();
      console.log(`✅ apiRequest success for /api/${normalizedEndpoint}:`, data);
      return data;
    } catch (parseError) {
      // В некоторых случаях ответ без тела (204 / пустой 200)
      console.log(`✅ apiRequest success for /api/${normalizedEndpoint} (no JSON body)`);
      return {};
    }
  } catch (error) {
    // Очищаем таймаут в случае ошибки
    clearTimeout(timeoutId);
    
    // Обрабатываем ошибку аборта как таймаут
    if (isAbortLikeError(error)) {
      const timeoutMinutes = Math.round(timeoutMs / 60000);
      if (!quietErrors) {
        console.error(`⏰ Timeout for /api/${normalizedEndpoint}`);
      }
      throw new Error(`Превышен лимит времени обработки (${timeoutMinutes} минут). Попробуйте позже или разбейте операцию на части.`);
    }

    const isExpectedLogin401 = error?.status === 401 && error?.endpoint === 'auth/login';
    if (!isExpectedLogin401 && !quietErrors) {
      console.error(`❌ API request error for /api/${normalizedEndpoint}:`, error);
    }
    throw error;
  }
};

// API методы для аутентификации
export const authAPI = {
  register: async (userData) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    return response;
  },

  login: async (email, password, altchaPayload = null, companyCode = null) => {
    const loginData = { email, password };
    if (companyCode) {
      loginData.company_code = companyCode;
    }
    if (altchaPayload) {
      loginData.altcha_payload = altchaPayload;
    }
    
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginData)
    });
    
    if (response.access_token) {
      await persistAuthSession(response);
    }
    
    return response;
  },

  startCompanyAdfsLogin: async (companyCode) => {
    return await apiRequest(`/auth/sso/adfs/start?company_code=${encodeURIComponent(companyCode)}`);
  },

  completeCompanyAdfsLogin: async (exchange) => {
    const response = await apiRequest('/auth/sso/adfs/exchange', {
      method: 'POST',
      body: JSON.stringify({ exchange }),
    });
    if (response.access_token) {
      await persistAuthSession(response);
    }
    return response;
  },

  logout: async () => {
    try {
      await apiRequest('/auth/logout');
    } finally {
      clearStoredAuthSession();
    }
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiRequest('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (response.access_token) {
      localStorage.setItem('accessToken', response.access_token);
      localStorage.setItem('refreshToken', response.refresh_token);
    }

    return response;
  },

  getProfile: async () => {
    const profile = await apiRequest('/auth/profile');
    applyProfileToStorage(profile);
    return profile;
  },

  getAuthMode: async (login = null, companyCode = null) => {
    const params = new URLSearchParams();
    if (login) params.set('login', login);
    if (companyCode) params.set('company_code', companyCode);
    const query = params.toString();
    return await apiRequest(`/auth/mode${query ? `?${query}` : ''}`, {
      quietErrors: true
    });
  },

  forgotPassword: async (email) => {
    console.log('📤 authAPI.forgotPassword called with email:', email);
    try {
      const result = await apiRequest('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      console.log('✅ authAPI.forgotPassword success:', result);
      return result;
    } catch (error) {
      console.error('❌ authAPI.forgotPassword error:', error);
      throw error;
    }
  },

  resetPassword: async (token, password) => {
    const response = await apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: password })
    });
    if (response.access_token) {
      await persistAuthSession(response);
    }
    return response;
  },

  verifyResetToken: async (token) => {
    return await apiRequest(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
  },

  verifyEmail: async (token) => {
    return await apiRequest(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  },

  getRegistrationOfferStatus: async () => {
    return await apiRequest('/auth/registration-offer-status', {
      quietErrors: true
    });
  },

  markRegistrationOfferModalShown: async () => {
    return await apiRequest('/auth/registration-offer/modal-shown', {
      method: 'POST'
    });
  },

  resendVerificationEmail: async (email) => {
    return await apiRequest('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  // Проверка, заблокирован ли почтовый домен (публичный)
  checkEmailDomain: async (email) => {
    const response = await fetch(`/api/auth/check-email-domain?email=${encodeURIComponent(email)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  // Получение списка заблокированных доменов (публичный)
  getBlockedDomains: async () => {
    const response = await fetch('/api/auth/blocked-domains');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  },

  getCurrentUser: async (options = {}) => {
    const profile = await apiRequest('/auth/me', options);
    applyProfileToStorage(profile);
    return profile;
  },

  restoreAdminSession: async () => {
    const response = await apiRequest('/auth/impersonation/restore', {
      method: 'POST'
    });

    if (response.access_token) {
      await persistAuthSession(response);
    }

    return response;
  },

  updateProfile: async (profileData) => {
    return await apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest('/auth/profile/avatar', {
      method: 'POST',
      body: formData
    });
  },

  deleteAvatar: async () => {
    return await apiRequest('/auth/profile/avatar', {
      method: 'DELETE'
    });
  },

  changePassword: async (passwordData) => {
    return await apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  },

  // Получение истории входов
  getLoginHistory: async (page = 1, pageSize = 10) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    return await apiRequest(`/auth/login-history?${params.toString()}`);
  },

  exportLoginHistoryCsv: async () => {
    const response = await fetch('/api/auth/login-history?format=csv', {
      headers: {
        ...getAuthHeaders(),
        'Accept': 'text/csv'
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ошибка экспорта истории входов`);
    }
    return await response.blob();
  },

  getMCPRequestsHistory: async (page = 1, pageSize = 10) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    return await apiRequest(`/auth/mcp-requests-history?${params.toString()}`);
  },

  exportMCPRequestsHistoryCsv: async () => {
    const response = await fetch('/api/auth/mcp-requests-history?format=csv', {
      headers: {
        ...getAuthHeaders(),
        'Accept': 'text/csv'
      }
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ошибка экспорта истории MCP-запросов`);
    }
    return await response.blob();
  },

  // API методы для работы с порядком карточек на дашборде
  getDashboardOrder: async () => {
    return await apiRequest('/auth/dashboard-order');
  },

  updateDashboardOrder: async (appsOrder) => {
    return await apiRequest('/auth/dashboard-order', {
      method: 'PUT',
      body: JSON.stringify({ apps_order: appsOrder })
    });
  },

  getDashboardOnboarding: async () => {
    return await apiRequest('/auth/dashboard-onboarding');
  },

  updateDashboardOnboarding: async (completed) => {
    return await apiRequest('/auth/dashboard-onboarding', {
      method: 'PUT',
      body: JSON.stringify({ completed })
    });
  },

  // API методы для статистики хранилища
  getStorageStats: async () => {
    return await apiRequest('/auth/storage-stats');
  },

  // Получение списка файлов пользователя
  getUserFiles: async (source = null, page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page, page_size: pageSize });
    if (source) params.append('source', source);
    return await apiRequest(`/auth/user-files?${params}`);
  },

  // Удаление файла пользователя
  deleteUserFile: async (fileId, source) => {
    return await apiRequest(`/auth/user-files/${fileId}?source=${source}`, {
      method: 'DELETE'
    });
  },

  // Методы для работы с тикетами поддержки
  getSupportTickets: async (page = 1, pageSize = 10, status = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (status) {
      params.append('status', status);
    }
    
    return await apiRequest(`/support/tickets?${params.toString()}`);
  },

  getSupportTicketDetails: async (ticketId) => {
    return await apiRequest(`/support/tickets/${ticketId}`);
  },

  createSupportTicket: async (ticketData) => {
    return await apiRequest('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData)
    });
  },

  addSupportTicketMessage: async (ticketId, messageData) => {
    return await apiRequest(`/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  },

  getSupportTicketStats: async () => {
    return await apiRequest('/support/tickets/stats');
  },

  // Методы для администраторов
  getAdminSupportTickets: async (page = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    if (filters.priority) {
      params.append('priority', filters.priority);
    }
    
    if (filters.assigned_to) {
      params.append('assigned_to', filters.assigned_to);
    }
    
    if (filters.user_id) {
      params.append('user_id', filters.user_id);
    }
    
    return await apiRequest(`/support/admin/tickets?${params.toString()}`);
  },

  updateSupportTicket: async (ticketId, updateData) => {
    return await apiRequest(`/support/admin/tickets/${ticketId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  resolveSupportTicket: async (ticketId, resolutionData) => {
    return await apiRequest(`/support/admin/tickets/${ticketId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(resolutionData)
    });
  },

  getAdminSupportTicketStats: async () => {
    return await apiRequest('/support/admin/tickets/stats');
  },

  // ============ API КЛЮЧИ ============
  
  // Генерация нового API ключа
  generateApiKey: async (name, expiresDays = null) => {
    return await apiRequest('/api-keys/generate', {
      method: 'POST',
      body: JSON.stringify({ 
        name: name,
        expires_days: expiresDays 
      })
    });
  },

  // Получение списка API ключей
  getApiKeys: async (page = 1, limit = 20, activeOnly = true) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      active_only: activeOnly.toString()
    });
    return await apiRequest(`/api-keys/list?${params.toString()}`);
  },

  // Активация/деактивация API ключа
  toggleApiKey: async (keyId) => {
    return await apiRequest(`/api-keys/${keyId}/toggle`, {
      method: 'PUT'
    });
  },

  // Удаление API ключа
  deleteApiKey: async (keyId) => {
    return await apiRequest(`/api-keys/${keyId}`, {
      method: 'DELETE'
    });
  },

  // Получение полного API ключа (расшифровка)
  revealApiKey: async (keyId) => {
    return await apiRequest(`/api-keys/${keyId}/reveal`);
  },

  // Получение статистики API ключа
  getApiKeyStats: async (keyId, days = 30) => {
    return await apiRequest(`/api-keys/${keyId}/stats?days=${days}`);
  }
};

// API методы для транскрибации
export const transcriptionAPI = {
  upload: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.language) formData.append('language', options.language);
    if (options.enable_diarization) formData.append('enable_diarization', options.enable_diarization);
    if (options.num_speakers) formData.append('num_speakers', options.num_speakers);

    // Используем специальную функцию для загрузки файлов с увеличенным таймаутом
    return await uploadFileRequest('/transcription/upload', formData);
  },

  // Загрузка файла по внешней ссылке
  uploadFromUrl: async (url, options = {}) => {
    const body = {
      url: url,
      language: options.language || 'ru',
      enable_diarization: options.enable_diarization !== false
    };

    // Используем увеличенный таймаут для скачивания файлов (60 минут)
    // Файлы могут быть большими, а серверы - медленными
    const token = localStorage.getItem('accessToken');
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => abortWithTimeoutReason(controller, 'Таймаут загрузки файла по ссылке'),
      3600000
    ); // 60 минут

    try {
      const response = await fetch('/api/transcription/upload-from-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (isAbortLikeError(error)) {
        throw new Error('Таймаут при загрузке файла по ссылке. Попробуйте позже.');
      }
      throw error;
    }
  },

  // Получение прогресса скачивания файла по URL
  getDownloadProgress: async (transcriptionId) => {
    return await apiRequest(`/transcription/download-progress/${transcriptionId}`);
  },
  vocabulary: {
    list: async () => {
      return await apiRequest(`/transcription/vocabulary`);
    },
    add: async ({ term, language = null }) => {
      return await apiRequest(`/transcription/vocabulary`, {
        method: 'POST',
        body: JSON.stringify({ term, language })
      });
    },
    addBulk: async (terms = [], language = null) => {
      return await apiRequest(`/transcription/vocabulary/bulk`, {
        method: 'POST',
        body: JSON.stringify({ terms, language })
      });
    },
    remove: async (id) => {
      return await apiRequest(`/transcription/vocabulary/${id}`, {
        method: 'DELETE'
      });
    }
  },

  getStatus: async (transcriptionId) => {
    return await apiRequest(`/transcription/status/${transcriptionId}`);
  },

  startVksAgentSession: async (payload = {}) => {
    return await apiRequest('/transcription/vks-agent/start', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeout: 45000
    });
  },

  getVksAgentStatus: async (sessionId) => {
    return await apiRequest(`/transcription/vks-agent/${sessionId}/status`);
  },

  stopVksAgentSession: async (sessionId) => {
    return await apiRequest(`/transcription/vks-agent/${sessionId}/stop`, {
      method: 'POST'
    });
  },

  retryVksAgentTranscription: async (sessionId) => {
    return await apiRequest(`/transcription/vks-agent/${sessionId}/retry-transcription`, {
      method: 'POST'
    });
  },

  getVksAgentHistory: async (page = 1, limit = 10, options = {}) => {
    return await throttledGetRequest(
      `/transcription/vks-agent/history?page=${page}&limit=${limit}`,
      3000,
      options
    );
  },

  vksScheduler: {
    getOverview: async (weekOffset = 0, upcomingLimit = 5) => {
      return await apiRequest(`/transcription/vks-scheduler/overview?week_offset=${weekOffset}&upcoming_limit=${upcomingLimit}`);
    },
    create: async (payload = {}) => {
      return await apiRequest('/transcription/vks-scheduler/schedules', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },
    update: async (scheduleId, payload = {}) => {
      return await apiRequest(`/transcription/vks-scheduler/schedules/${scheduleId}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
    },
    remove: async (scheduleId) => {
      return await apiRequest(`/transcription/vks-scheduler/schedules/${scheduleId}`, {
        method: 'DELETE'
      });
    },
    stopAgent: async (scheduleId) => {
      return await apiRequest(`/transcription/vks-scheduler/schedules/${scheduleId}/stop-agent`, {
        method: 'POST'
      });
    }
  },

  getResult: async (transcriptionId) => {
    return await apiRequest(`/transcription/result/${transcriptionId}`);
  },

  updateResult: async (transcriptionId, payload = {}) => {
    return await apiRequest(`/transcription/result/${transcriptionId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  downloadSubtitledVideo: async (transcriptionId) => {
    let token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    let response = await fetch(`/api/transcription/download/${transcriptionId}/subtitled-video`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      }
    });

    if (response.status === 401) {
      const newToken = await refreshAuthToken();
      if (!newToken) {
        throw new Error('Сессия истекла');
      }
      token = newToken;
      response = await fetch(`/api/transcription/download/${transcriptionId}/subtitled-video`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }

    if (!response.ok) {
      let detail = '';
      let errorText = '';
      try {
        errorText = await response.text();
        const parsed = errorText ? JSON.parse(errorText) : null;
        detail = parsed?.detail || errorText;
      } catch (e) {
        detail = errorText;
      }
      throw new Error(detail || `HTTP ${response.status}`);
    }

    return response;
  },

  getMediaUrl: (transcriptionId) => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    return `/api/transcription/media/${transcriptionId}${tokenQuery}`;
  },

  getMediaBlob: async (transcriptionId) => {
    let token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    let response = await fetch(`/api/transcription/media/${transcriptionId}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      }
    });

    if (response.status === 401) {
      const newToken = await refreshAuthToken();
      if (!newToken) {
        throw new Error('Сессия истекла');
      }
      token = newToken;
      response = await fetch(`/api/transcription/media/${transcriptionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }

    if (!response.ok) {
      let detail = '';
      try {
        detail = await response.text();
      } catch (e) {
        detail = '';
      }
      throw new Error(detail || `HTTP ${response.status}`);
    }

    return await response.blob();
  },

  getSpeakers: async (transcriptionId) => {
    return await apiRequest(`/transcription/speakers/${transcriptionId}`);
  },

  updateSpeakers: async (transcriptionId, speakers = []) => {
    return await apiRequest(`/transcription/speakers/${transcriptionId}`, {
      method: 'PUT',
      body: JSON.stringify({ speakers })
    });
  },

  getHistory: async (page = 1, limit = 10, search = '') => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    });
    const normalizedSearch = String(search || '').trim();
    if (normalizedSearch) {
      params.set('search', normalizedSearch);
    }
    return await apiRequest(`/transcription/history?${params.toString()}`);
  },

  updateDisplayName: async (transcriptionId, displayName) => {
    return await apiRequest(`/transcription/${transcriptionId}/display-name`, {
      method: 'PUT',
      body: JSON.stringify({ display_name: displayName })
    });
  },

  createShare: async (payload = {}) => {
    return await apiRequest('/transcription/shares', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  listShares: async (transcriptionId) => {
    return await apiRequest(`/transcription/shares/${transcriptionId}`);
  },

  updateShare: async (shareId, payload = {}) => {
    return await apiRequest(`/transcription/shares/${shareId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  revokeShare: async (shareId) => {
    return await apiRequest(`/transcription/shares/${shareId}`, {
      method: 'DELETE'
    });
  },

  getGraphTaskStatus: async (taskId) => {
    return await apiRequest(`/transcription/graph-task-status/${taskId}`);
  },
  retryGraphTask: async (transcriptionId) => {
    return await apiRequest(`/transcription/graph-task/${transcriptionId}/retry`, {
      method: 'POST'
    });
  },


  download: async (transcriptionId, format = 'json') => {
    //const baseURL = process.env.REACT_APP_API_URL || '/api';
    
    const token = localStorage.getItem('accessToken');
    
    const config = {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };

    // Если токен истек, пытаемся обновить его
    const response = await fetch(`/api/transcription/download/${transcriptionId}?format=${format}`, config);
    
    if (response.status === 401 && token) {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResponse = await fetch(`/api/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken })
          });

          if (refreshResponse.ok) {
            const tokenData = await refreshResponse.json();
            localStorage.setItem('accessToken', tokenData.access_token);
            localStorage.setItem('refreshToken', tokenData.refresh_token);
            
            // Повторяем запрос с новым токеном
            config.headers.Authorization = `Bearer ${tokenData.access_token}`;
            const retryResponse = await fetch(`/api/transcription/download/${transcriptionId}?format=${format}`, config);
            
            if (retryResponse.ok) {
              return retryResponse; // Возвращаем Response объект для .blob()
            }
          }
        }
        
        // Если обновление токена не удалось, очищаем токены и редиректим на логин
        clearStoredAuthSession();
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        throw new Error('Требуется повторная авторизация');
      } catch (refreshError) {
        clearStoredAuthSession();
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        throw new Error('Сессия истекла');
      }
    }

    if (!response.ok) {
      // Пытаемся прочитать ошибку как JSON
      try {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      } catch (e) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return response; // Возвращаем Response объект для .blob()
  },

  delete: async (transcriptionId) => {
    return await apiRequest(`/transcription/${transcriptionId}`, {
      method: 'DELETE'
    });
  }
};

const publicShareRequest = async (endpoint, options = {}) => {
  const response = await fetch(`/api/${normalizeEndpoint(endpoint)}`, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}),
      ...(options.headers || {})
    },
    body: options.body
  });

  if (!response.ok) {
    const errorText = await response.text();
    let message = errorText || `HTTP ${response.status}`;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.detail || parsed.message || message;
    } catch (error) {
      // Оставляем текст ответа как сообщение об ошибке.
    }
    throw new Error(message);
  }

  return await response.json();
};

export const transcriptionShareAPI = {
  getMeta: async (token) => {
    return await publicShareRequest(`/transcription/shares/public/${token}/meta`);
  },

  unlock: async (token, password) => {
    return await publicShareRequest(`/transcription/shares/public/${token}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ password })
    });
  },

  getContent: async (token, accessToken) => {
    return await publicShareRequest(`/transcription/shares/public/${token}/content`, {
      accessToken
    });
  },

  getMediaUrl: (token, accessToken, download = false) => {
    const params = new URLSearchParams();
    if (accessToken) params.set('access_token', accessToken);
    if (download) params.set('download', 'true');
    const query = params.toString();
    return `/api/transcription/shares/public/${token}/media${query ? `?${query}` : ''}`;
  },

  getTranscriptionDownloadUrl: (token, accessToken, format = 'txt') => {
    const params = new URLSearchParams();
    params.set('format', format);
    if (accessToken) params.set('access_token', accessToken);
    return `/api/transcription/shares/public/${token}/download/transcription?${params.toString()}`;
  },

  getAIResponseDownloadUrl: (token, accessToken, resource = 'protocol', format = 'txt') => {
    const params = new URLSearchParams();
    params.set('resource', resource);
    params.set('format', format);
    if (accessToken) params.set('access_token', accessToken);
    return `/api/transcription/shares/public/${token}/download/ai-response?${params.toString()}`;
  }
};

// API методы для текстовых операций
export const textAPI = {
  summarize: async (text, options = {}) => {
    return await longRunningApiRequest('/text/summarize', {
      method: 'POST',
      body: JSON.stringify({ text, ...options })
    });
  },

  // Проверка статуса задачи
  checkTaskStatus: async (taskId) => {
    return await quickApiRequest(`/text/task-status/${taskId}`, {}, 10);
  },

  // Ожидание завершения задачи с polling
  waitForTaskCompletion: async (taskId, maxWaitMinutes = 180) => {
    console.log(`⏳ Начинаем ожидание завершения задачи: ${taskId}`);
    const startTime = Date.now();
    const maxWaitMs = maxWaitMinutes * 60 * 1000;
    let attemptCount = 0;
    
    // Небольшая начальная задержка перед первой проверкой (даем бэкенду время начать обработку)
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    while (Date.now() - startTime < maxWaitMs) {
      attemptCount++;
      try {
        console.log(`🔄 Проверка статуса задачи ${taskId} (попытка ${attemptCount})`);
        const status = await textAPI.checkTaskStatus(taskId);
        
        console.log(`📊 Статус задачи:`, status);
        
        if (status.status === 'completed') {
          console.log(`✅ Задача ${taskId} завершена успешно`);
          
          // Извлекаем результат из разных возможных структур
          const resultData = status.result || status;
          const resultText = status.result_text || 
                            (resultData.result?.result) || 
                            resultData.result || 
                            resultData.response || 
                            resultData.formatted_response;
          
          console.log(`📄 Извлеченный результат (длина):`, resultText?.length || 0);
          
          return {
            success: true,
            result: resultText,
            response_id: resultData.response_id || status.response_id,
            model: resultData.model || status.model,
            tokens_used: resultData.tokens_used || status.tokens_used,
            processing_time: resultData.processing_time || status.processing_time
          };
        } else if (status.status === 'failed') {
          console.error(`❌ Задача ${taskId} завершена с ошибкой:`, status.error);
          throw new Error(`Ошибка обработки: ${status.error || 'Неизвестная ошибка'}`);
        } else if (status.status === 'processing') {
          console.log(`⏳ Задача ${taskId} еще обрабатывается...`);
        } else {
          console.log(`⏳ Задача ${taskId} в статусе: ${status.status}`);
        }
        
        // Увеличиваем интервал проверки со временем
        let waitTime = Math.min(5000 + (attemptCount * 1000), 30000); // от 6 до 30 секунд
        console.log(`⏰ Ждем ${waitTime/1000} секунд до следующей проверки...`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
      } catch (error) {
        console.error(`❌ Ошибка проверки статуса задачи ${taskId}:`, error);
        
        // Если это ошибка сети, ждем и повторяем
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          console.log(`🔄 Ошибка сети, повторяем через 10 секунд...`);
          await new Promise(resolve => setTimeout(resolve, 10000));
          continue;
        }
        
        throw error;
      }
    }
    
    // Таймаут
    throw new Error(`Время ожидания истекло (${maxWaitMinutes} минут). Задача может еще обрабатываться. Проверьте историю ИИ.`);
  },

  meetingProtocol: async (text, options = {}) => {
    // Сначала пробуем backend обработку (умное разбиение + объединение)
    // Frontend разбивка только для экстренных случаев сетевых ограничений
    try {
      // Детальная диагностика запроса
      const requestBody = JSON.stringify({ text, ...options });
      const requestSize = new Blob([requestBody]).size;
      
      console.log(`🔍 ДИАГНОСТИКА ЗАПРОСА:`);
      console.log(`📄 Текст: ${text.length.toLocaleString()} символов`);
      console.log(`📦 JSON размер: ${requestSize.toLocaleString()} байт (${(requestSize/1024).toFixed(1)} KB)`);
      console.log(`🎯 Отправляем на backend: /text/meeting-protocol`);
      
      const taskResponse = await longRunningApiRequest('/text/meeting-protocol', {
        method: 'POST',
        body: requestBody
      });
      
      console.log(`✅ Задача создана:`, taskResponse);
      
      // Если получили task_id, ждем завершения
      if (taskResponse.task_id) {
        return {
          success: true,
          task_id: taskResponse.task_id,
          status: 'processing',
        };
      }
      
      // Для обратной совместимости - если результат пришел сразу
      console.log(`🔍 Детали ответа:`, {
        success: taskResponse.success,
        hasResult: !!taskResponse.result,
        hasResponseId: !!taskResponse.response_id,
        testMode: taskResponse.test_mode,
        resultLength: taskResponse.result?.length,
        model: taskResponse.model
      });
      return taskResponse;
      
    } catch (error) {
      console.log(`❌ Backend обработка не удалась:`, {
        message: error.message,
        name: error.name,
        stack: error.stack?.substring(0, 500)
      });
      
      // Если ошибка сети и большой документ - пробуем frontend chunking
      if ((error.message.includes('Failed to fetch') || 
           error.message.includes('NetworkError') || 
           error.message.includes('AbortError')) && 
          text.length > 30000) {
        
        console.log(`🔄 Переходим на frontend chunking как запасной вариант`);
        
        // Разбиваем текст на части по 20KB для безопасности
        const chunkSize = 20000;
        const chunks = [];
        for (let i = 0; i < text.length; i += chunkSize) {
          chunks.push(text.slice(i, i + chunkSize));
        }
        
        console.log(`📋 Разбито на ${chunks.length} частей, обрабатываем по очереди`);
        
        // Обрабатываем каждую часть отдельно
        const results = [];
        for (let i = 0; i < chunks.length; i++) {
          console.log(`🔄 Обработка части ${i + 1}/${chunks.length}`);
          
          const chunkResult = await longRunningApiRequest('/text/meeting-protocol', {
            method: 'POST',
            body: JSON.stringify({ 
              text: chunks[i], 
              chunk_info: {
                chunk_number: i + 1,
                total_chunks: chunks.length,
                is_chunk: true
              },
              ...options 
            })
          });
          
          results.push(chunkResult.result);
        }
        
        // Объединяем результаты
        const combinedResult = `# ПРОТОКОЛ СОВЕЩАНИЯ
        
${results.map((result, index) => `## Часть ${index + 1}

${result}`).join('\n\n')}

---
*Документ обработан по частям для обеспечения стабильности (${chunks.length} частей, ${text.length.toLocaleString()} символов)*`;
        
        return {
          success: true,
          result: combinedResult,
          response_id: null, // Нет реального ID, так как это frontend chunking
          tokens_used: results.reduce((sum, r) => sum + (r.tokens_used || 0), 0),
          model: "frontend_chunked",
          is_chunked: true,
          is_frontend_chunked: true, // Флаг для отключения скачивания
          chunks_processed: chunks.length
        };
      }
      
      // Перебрасываем ошибку если это не сетевая проблема
      throw error;
    }
  },

  hrAnalysis: async (text, options = {}) => {
    return await longRunningApiRequest('/text/hr-analysis', {
      method: 'POST',
      body: JSON.stringify({ text, ...options })
    });
  },

  customInstruction: async (text, prompt, options = {}) => {
    return await longRunningApiRequest('/text/custom-instruction', {
      method: 'POST',
      body: JSON.stringify({ text, prompt, ...options })
    });
  },

  meetingProtocolRAG: async (transcriptionId, options = {}) => {
    if (!transcriptionId) {
      throw new Error('Требуется transcription_id для RAG-протокола');
    }

    const requestBody = JSON.stringify({
      transcription_id: transcriptionId,
      ...options
    });

    const response = await apiRequest('/transcription/generate-graph-protocol', {
      method: 'POST',
      body: requestBody,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response && Object.keys(response).length > 0) {
      return response;
    }

    return {
      success: true,
      task_id: null,
      status: 'processing',
      message: 'Генерация протокола запущена',
      method: 'graph-service',
      transcription_id: transcriptionId
    };
  },

  checkOllama: async () => {
    return await apiRequest('/text/check-ollama', { timeout: 10000 });
  },

  // История ответов ИИ
  getAIHistory: async (page = 1, limit = 20, operationType = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (operationType) {
      params.append('operation_type', operationType);
    }
    
    return await apiRequest(`/text/ai-responses/history?${params}`);
  },

  getAIResponse: async (responseId) => {
    return await apiRequest(`/text/ai-responses/${responseId}`);
  },

  saveAIResponseManualEdit: async (responseId, content) => {
    return await apiRequest(`/text/ai-responses/${responseId}/manual-edit`, {
      method: 'PUT',
      body: JSON.stringify({ content })
    });
  },

  // Получение текстовых операций для конкретной транскрибации
  getTextOperationsByTranscription: async (transcriptionId) => {
    return await apiRequest(`/text/transcription/${transcriptionId}/operations`);
  },

  // Оценка стоимости обработки текста
  estimateCost: async (text) => {
    return await apiRequest('/text/estimate-cost', {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  },

  downloadAIResponse: async (responseId, format = 'docx', variant = 'system') => {
    //const baseURL = process.env.REACT_APP_API_URL || '/api';
    
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Требуется авторизация для скачивания файла');
    }

    const params = new URLSearchParams();
    params.set('format', format);
    params.set('variant', variant === 'user_edited' ? 'user_edited' : 'system');

    const response = await fetch(`/api/text/ai-responses/${responseId}/download?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Обработка истечения токена
    if (response.status === 401) {
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('Токен истек');
        }

        const refreshResponse = await fetch(`/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (refreshResponse.ok) {
          const tokenData = await refreshResponse.json();
          localStorage.setItem('accessToken', tokenData.access_token);
          localStorage.setItem('refreshToken', tokenData.refresh_token);
          
          // Повторяем запрос с новым токеном
          const retryResponse = await fetch(`/api/text/ai-responses/${responseId}/download?${params.toString()}`, {
            headers: {
              'Authorization': `Bearer ${tokenData.access_token}`
            }
          });
          
          if (retryResponse.ok) {
            return retryResponse;
          }
        }
        
        // Очищаем токены и перенаправляем на логин
        clearStoredAuthSession();
        window.location.href = '/login';
        throw new Error('Токен истек, требуется повторная авторизация');
      } catch (error) {
        console.error('Ошибка обновления токена:', error);
        throw error;
      }
    }

    if (!response.ok) {
      throw new Error('Ошибка скачивания файла');
    }

    return response; // Возвращаем Response объект для .blob()
  },

  // Тестирование больших POST запросов для диагностики
  debugRequest: async (text) => {
    return await longRunningApiRequest('/text/debug-request', {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  }
};

// API методы для биллинга
export const billingAPI = {
  getBalance: async () => {
    return await apiRequest('/billing/balance');
  },

  getHistory: async (page = 1, limit = 10) => {
    return await apiRequest(`/billing/history?page=${page}&limit=${limit}`);
  },

  getStats: async (days = 30) => {
    return await apiRequest(`/billing/stats?days=${days}`);
  },

  getTariffs: async () => {
    return await apiRequest('/billing/tariffs');
  },

  applyCoupon: async (couponCode) => {
    return await apiRequest('/billing/apply-coupon', {
      method: 'POST',
      body: JSON.stringify({ code: couponCode })
    });
  },

  // Админские методы для купонов
  createCoupon: async (couponData) => {
    return await apiRequest('/billing/admin/create-coupon', {
      method: 'POST',
      body: JSON.stringify(couponData)
    });
  },

  getAllCoupons: async (page = 1, limit = 50) => {
    return await apiRequest(`/billing/admin/coupons?page=${page}&limit=${limit}`);
  },

  addUserBalance: async (userData) => {
    return await apiRequest('/billing/admin/add-balance', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  deleteCoupon: async (couponId) => {
    return await apiRequest(`/billing/admin/coupons/${couponId}`, {
      method: 'DELETE'
    });
  },

  // ============ Billing v2: ИИ Кредиты ============
  
  // Получение подписки пользователя
  getSubscription: async () => {
    return await apiRequest('/billing/v2/subscription');
  },

  // Получение баланса v2 (AI кредиты)
  getBalanceV2: async () => {
    return await apiRequest('/billing/v2/balance');
  },

  getMyCompanyPublicModelQuota: async () => {
    return await apiRequest('/billing/v2/company-public-model-quota');
  },

  // Получение тарифных планов
  getPlans: async () => {
    return await apiRequest('/billing/v2/plans');
  },

  // Получение цен на операции
  getOperationPrices: async () => {
    return await apiRequest('/billing/v2/prices');
  },

  // Получение информации об оплате
  getPaymentInfo: async () => {
    return await apiRequest('/billing/v2/payment-info');
  },

  // Получение статистики v2
  getStatsV2: async (days = 30) => {
    return await apiRequest(`/billing/v2/stats?days=${days}`);
  },

  // Получение истории v2
  getHistoryV2: async (page = 1, limit = 50, filters = {}) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (filters.operationType) params.set('operation_type', filters.operationType);
    if (filters.requestSource) params.set('request_source', filters.requestSource);
    if (filters.model) params.set('model', filters.model);
    if (filters.modelKind) params.set('model_kind', filters.modelKind);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    return await apiRequest(`/billing/v2/history?${params.toString()}`);
  },

  // Получение статуса очереди
  getQueueStatus: async () => {
    return await apiRequest('/billing/v2/queue/status');
  },

  // Получение задач в очереди
  getQueueTasks: async (includeCompleted = false) => {
    return await apiRequest(`/billing/v2/queue/tasks?include_completed=${includeCompleted}`);
  },

  // Отмена задачи в очереди
  cancelQueueTask: async (taskId) => {
    return await apiRequest(`/billing/v2/queue/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  // Отмена подписки пользователя (перевод на базовый тариф)
  cancelSubscription: async (reason = '') => {
    return await apiRequest('/billing/v2/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  // Админ: обновление цены операции
  updateOperationPrice: async (operationType, data) => {
    return await apiRequest(`/billing/v2/admin/prices/${operationType}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Админ: установка триального периода
  setUserTrialPeriod: async (userId, trialDays) => {
    return await apiRequest('/billing/v2/admin/set-trial', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, trial_days: trialDays })
    });
  },

  // Админ: добавление кредитов
  addUserCredits: async (userId, credits, description = '') => {
    return await apiRequest('/billing/v2/admin/add-credits', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, credits, description })
    });
  },

  // Админ: разовое пополнение токенов публичных моделей
  addUserPublicTokens: async (userId, tokens, description = '') => {
    return await apiRequest('/billing/v2/admin/add-public-tokens', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, tokens, description })
    });
  },

  // Админ: персональные токены выбранной публичной модели
  getUserPublicModelTokens: async (userId) => {
    return await apiRequest(`/billing/v2/admin/users/${userId}/public-model-tokens`);
  },

  addUserPublicModelTokens: async (userId, modelId, tokens, description = '') => {
    return await apiRequest('/billing/v2/admin/add-public-model-tokens', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        model_id: modelId,
        tokens,
        description
      })
    });
  },

  // Админ: смена тарифа пользователя
  changeUserPlan: async (userId, planCode) => {
    return await apiRequest('/billing/v2/admin/change-plan', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, plan_code: planCode })
    });
  },

  setUserQueuePriority: async (userId, queuePriority) => {
    return await apiRequest('/billing/v2/admin/set-queue-priority', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, queue_priority: queuePriority })
    });
  },

  // Админ: массовое добавление кредитов выбранным пользователям
  bulkAddCredits: async (userIds, credits, description = '') => {
    return await apiRequest('/billing/v2/admin/bulk/add-credits', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, credits, description })
    });
  },

  // Админ: массовое добавление дней триала выбранным пользователям
  bulkAddTrialDays: async (userIds, trialDays) => {
    return await apiRequest('/billing/v2/admin/bulk/add-trial-days', {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds, trial_days: trialDays })
    });
  },

  // Админ: получение подписки пользователя
  getUserSubscription: async (userId) => {
    return await apiRequest(`/billing/v2/admin/users/${userId}/subscription`);
  },

  // Админ: подписки пользователей по всем тарифам
  getSubscriptionsByPlan: async (status = null, planCode = null, limit = 100, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.append('status', status);
    if (planCode) params.append('plan_code', planCode);
    return await apiRequest(`/billing/v2/admin/subscriptions-by-plan?${params}`);
  },

  // Админ: ручной запуск продления бесплатных тарифов с auto_renew
  renewFreeSubscriptionsNow: async () => {
    return await apiRequest('/billing/v2/admin/subscriptions/renew-free', {
      method: 'POST'
    });
  },

  // Админ: общий статус очереди
  getAdminQueueStatus: async () => {
    return await apiRequest('/billing/v2/admin/queue/status');
  },

  // ============ Админ: Управление тарифами ============

  // Получение списка всех тарифов (включая неактивные)
  getAdminPlans: async (includeInactive = true) => {
    return await apiRequest(`/billing/v2/admin/plans?include_inactive=${includeInactive}`);
  },

  // Создание нового тарифа
  createPlan: async (planData) => {
    return await apiRequest('/billing/v2/admin/plans', {
      method: 'POST',
      body: JSON.stringify(planData)
    });
  },

  // Обновление тарифа
  updatePlan: async (planId, planData) => {
    return await apiRequest(`/billing/v2/admin/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData)
    });
  },

  // Удаление тарифа
  deletePlan: async (planId) => {
    return await apiRequest(`/billing/v2/admin/plans/${planId}`, {
      method: 'DELETE'
    });
  },

  // ============ Robokassa: Платежи ============

  // Инициация платежа
  initPayment: async (planId, isRecurring = true) => {
    return await apiRequest('/v1/billing/robokassa/init-payment', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId, is_recurring: isRecurring })
    });
  },

  // Покупка дополнительных кредитов (5₽ за 1 кредит, мин. 10)
  buyCredits: async (creditsAmount) => {
    return await apiRequest('/v1/billing/robokassa/buy-credits', {
      method: 'POST',
      body: JSON.stringify({ credits_amount: creditsAmount })
    });
  },

  // Получение истории платежей
  getPayments: async (limit = 50, offset = 0) => {
    return await apiRequest(`/v1/billing/robokassa/payments?limit=${limit}&offset=${offset}`);
  },

  // Получение рекуррентных подписок
  getRecurringSubscriptions: async () => {
    return await apiRequest('/v1/billing/robokassa/subscriptions');
  },

  // Отмена подписки
  cancelRecurringSubscription: async (subscriptionId, reason = '') => {
    return await apiRequest('/v1/billing/robokassa/cancel-subscription', {
      method: 'POST',
      body: JSON.stringify({ subscription_id: subscriptionId, reason })
    });
  },

  // ============ Robokassa: Админ ============

  // Получение настроек Robokassa
  getRobokassaSettings: async () => {
    return await apiRequest('/v1/billing/robokassa/admin/settings');
  },

  // Сохранение настроек Robokassa
  saveRobokassaSettings: async (settings) => {
    return await apiRequest('/v1/billing/robokassa/admin/settings', {
      method: 'POST',
      body: JSON.stringify(settings)
    });
  },

  // Статистика платежей
  getPaymentStats: async (days = 30) => {
    return await apiRequest(`/v1/billing/robokassa/admin/stats?days=${days}`);
  },

  // Все платежи (админ)
  getAllPayments: async (status = null, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.append('status', status);
    return await apiRequest(`/v1/billing/robokassa/admin/payments?${params}`);
  },

  // Все подписки (админ)
  getAllSubscriptions: async (status = null, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit, offset });
    if (status) params.append('status', status);
    return await apiRequest(`/v1/billing/robokassa/admin/subscriptions?${params}`);
  },

  // Отмена подписки админом
  adminCancelSubscription: async (subscriptionId, reason = '') => {
    return await apiRequest(`/v1/billing/robokassa/admin/cancel-subscription/${subscriptionId}?reason=${encodeURIComponent(reason)}`, {
      method: 'POST'
    });
  },

  // Сброс периода подписки (для возможности повторной оплаты)
  adminResetSubscriptionPeriod: async (subscriptionId) => {
    return await apiRequest(`/v1/billing/robokassa/admin/reset-subscription-period/${subscriptionId}`, {
      method: 'POST'
    });
  },

  // Ручной запуск обработки просроченных рекуррентных списаний
  processRecurringPaymentsNow: async () => {
    return await apiRequest('/v1/billing/robokassa/admin/process-recurring-now', {
      method: 'POST'
    });
  },

  // Пересоздание платежа для подписки
  adminRecreatePayment: async (subscriptionId) => {
    return await apiRequest(`/v1/billing/robokassa/admin/recreate-payment/${subscriptionId}`, {
      method: 'POST'
    });
  },

  // Исправить все проблемные подписки
  adminFixAllRecurring: async () => {
    return await apiRequest('/v1/billing/robokassa/admin/fix-recurring-all', {
      method: 'POST'
    });
  },

  // Отправить ссылку на оплату по email
  adminSendPaymentLink: async (email, paymentUrl, invoiceId) => {
    const formData = new URLSearchParams();
    formData.append('email', email);
    formData.append('payment_url', paymentUrl);
    formData.append('invoice_id', invoiceId.toString());
    return await apiRequest('/v1/billing/robokassa/admin/send-payment-link', {
      method: 'POST',
      body: formData,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
  },

  // Получить список ожидающих оплат
  adminGetPendingRecurringPayments: async () => {
    return await apiRequest('/v1/billing/robokassa/admin/pending-recurring-payments');
  },

  // Восстановить подписку по invoice_id
  adminRestoreSubscription: async (invoiceId) => {
    return await apiRequest(`/v1/billing/robokassa/admin/restore-subscription/${invoiceId}`, {
      method: 'POST'
    });
  }
};

// API методы для API ключей
export const apiKeysAPI = {
  generate: async (name, expirationDays = null) => {
    return await apiRequest('/api-keys/generate', {
      method: 'POST',
      body: JSON.stringify({ name, expiration_days: expirationDays })
    });
  },

  list: async () => {
    return await apiRequest('/api-keys/list');
  },

  delete: async (keyId) => {
    return await apiRequest(`/api-keys/${keyId}`, {
      method: 'DELETE'
    });
  }
};

// API методы для настроек раздела "Сервис"
export const serviceMenuAPI = {
  getSettings: async () => {
    return await apiRequest('/service-menu/settings');
  }
};

export const knowledgeBaseAPI = {
  getArticles: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return await apiRequest(`/knowledge-base/articles${params}`);
  },

  getArticle: async (slug) => {
    return await apiRequest(`/knowledge-base/articles/${encodeURIComponent(slug)}`);
  }
};

// API методы для администрирования
export const adminAPI = {
  getTranscriptionSettings: async () => {
    return await apiRequest('/admin/transcription/settings');
  },

  updateTranscriptionSettings: async (data) => {
    return await apiRequest('/admin/transcription/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  getVidzheySettings: async () => {
    return await apiRequest('/admin/vidzhey/settings');
  },

  updateVidzheySettings: async (data) => {
    return await apiRequest('/admin/vidzhey/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  getActiveVidzheyRooms: async () => {
    return await apiRequest('/admin/vidzhey/active-rooms', { quietErrors: true });
  },

  stopActiveVidzheyRoom: async (roomId) => {
    return await apiRequest(`/admin/vidzhey/active-rooms/${roomId}/stop`, {
      method: 'POST'
    });
  },

  getVidzheyProblemSessions: async () => {
    return await apiRequest('/admin/vidzhey/problem-sessions', { quietErrors: true });
  },

  recoverVidzheyProblemSession: async (sessionId) => {
    return await apiRequest(`/admin/vidzhey/problem-sessions/${sessionId}/recover`, {
      method: 'POST'
    });
  },

  getVidzheyParticipantRecordings: async ({ page = 1, dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return await apiRequest(`/admin/vidzhey/participant-recordings?${params.toString()}`, { quietErrors: true });
  },

  deleteVidzheyParticipantRecordings: async (sessionId) => {
    return await apiRequest(`/admin/vidzhey/participant-recordings/${sessionId}`, {
      method: 'DELETE'
    });
  },

  deleteVidzheyParticipantRecordingsBulk: async (sessionIds) => {
    return await apiRequest('/admin/vidzhey/participant-recordings/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ session_ids: sessionIds })
    });
  },

  getTranscriptionHealth: async () => {
    return await apiRequest('/admin/transcription/health');
  },

  getServiceMenuSettings: async () => {
    return await apiRequest('/admin/service-menu/settings');
  },

  updateServiceMenuSettings: async (data) => {
    return await apiRequest('/admin/service-menu/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  getDashboardOnboardingSteps: async () => {
    return await apiRequest('/admin/dashboard-onboarding/steps');
  },

  createDashboardOnboardingStep: async (data) => {
    return await apiRequest('/admin/dashboard-onboarding/steps', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateDashboardOnboardingStep: async (stepId, data) => {
    return await apiRequest(`/admin/dashboard-onboarding/steps/${stepId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteDashboardOnboardingStep: async (stepId) => {
    return await apiRequest(`/admin/dashboard-onboarding/steps/${stepId}`, {
      method: 'DELETE'
    });
  },

  getAuthSettings: async () => {
    return await apiRequest('/admin/auth/settings');
  },

  updateAuthSettings: async (data) => {
    return await apiRequest('/admin/auth/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  testAuthConnection: async (data) => {
    return await apiRequest('/admin/auth/test-connection', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Пользователи
  getUsers: async (page = 1, limit = 10, search = '', roleFilter = '', lastLoginFilter = '', period = null) => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    if (roleFilter) params.append('role_filter', roleFilter);
    if (lastLoginFilter) params.append('last_login_filter', lastLoginFilter);
    if (period?.startDate && period?.endDate) {
      params.append('start_date', period.startDate);
      params.append('end_date', period.endDate);
      params.append('period_field', period.field || 'created_at');
    }
    
    return await apiRequest(`/admin/users?${params}`);
  },

  createUser: async (userData) => {
    return await apiRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  createUsersBulk: async (users) => {
    return await apiRequest('/admin/users/bulk', {
      method: 'POST',
      body: JSON.stringify({ users })
    });
  },

  exportUsersToXlsx: async (search = '', roleFilter = '', lastLoginFilter = '', period = null) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (roleFilter) params.append('role_filter', roleFilter);
    if (lastLoginFilter) params.append('last_login_filter', lastLoginFilter);
    if (period?.startDate && period?.endDate) {
      params.append('start_date', period.startDate);
      params.append('end_date', period.endDate);
      params.append('period_field', period.field || 'created_at');
    }
    
    const url = `/admin/users/export?${params}`;
    
    try {
      // Используем правильный ключ токена, как в apiRequest
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`/api/${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Проверяем, если ответ HTML (страница входа), значит проблема с авторизацией
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Проблема с авторизацией. Возможно, срок действия токена истек. Попробуйте обновить страницу и войти снова.');
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        let message = errorText;
        try {
          const parsed = JSON.parse(errorText);
          if (typeof parsed?.detail === 'string' && parsed.detail) {
            message = parsed.detail;
          }
        } catch (parseError) {
          message = errorText;
        }
        throw new Error(message || `Ошибка HTTP: ${response.status}`);
      }
      
      // Проверяем, что получен правильный тип контента
      if (!contentType || !contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        const responseText = await response.text();
        throw new Error(`Неверный тип контента: ${contentType}. Ответ: ${responseText.substring(0, 200)}`);
      }
      
      // Получаем имя файла из заголовка Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'users_export.xlsx';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      // Создаем Blob из ответа
      const blob = await response.blob();
      
      // Проверяем, что blob не пустой
      if (blob.size === 0) {
        throw new Error('Получен пустой файл');
      }
      
      // Проверяем минимальный размер для XLSX файла
      if (blob.size < 1000) {
        throw new Error('Файл слишком маленький, возможно он поврежден');
      }
      
      // Создаем ссылку для скачивания
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Очистка
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      return { success: true, filename, size: blob.size };
    } catch (error) {
      console.error('Ошибка при выгрузке пользователей:', error);
      throw error;
    }
  },

  getUserDetails: async (userId) => {
    return await apiRequest(`/admin/users/${userId}`);
  },

  updateUser: async (userId, userData) => {
    return await apiRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },

  getAdminRoles: async () => {
    return await apiRequest('/admin/roles');
  },

  createAdminRole: async (roleData) => {
    return await apiRequest('/admin/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  },

  updateAdminRole: async (roleId, roleData) => {
    return await apiRequest(`/admin/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(roleData)
    });
  },

  deleteAdminRole: async (roleId) => {
    return await apiRequest(`/admin/roles/${roleId}`, {
      method: 'DELETE'
    });
  },

  getUserAdminRoles: async (userId) => {
    return await apiRequest(`/admin/users/${userId}/admin-roles`);
  },

  updateUserAdminRoles: async (userId, roleIds) => {
    return await apiRequest(`/admin/users/${userId}/admin-roles`, {
      method: 'PUT',
      body: JSON.stringify({ role_ids: roleIds })
    });
  },

  changeUserPassword: async (userId, passwordData) => {
    return await apiRequest(`/admin/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  },

  resendUserPasswordSetup: async (userId) => {
    return await apiRequest(`/admin/users/${userId}/password-setup`, {
      method: 'POST'
    });
  },

  updateUserGroups: async (userId, groupIds = []) => {
    return await apiRequest(`/admin/users/${userId}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ group_ids: groupIds })
    });
  },

  deleteUser: async (userId) => {
    return await apiRequest(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  },

  impersonateUser: async (userId) => {
    const response = await apiRequest(`/admin/users/${userId}/impersonate`, {
      method: 'POST'
    });

    if (response.access_token) {
      await persistAuthSession(response);
    }

    return response;
  },

  getUserGroups: async (includeMembers = false) => {
    const query = includeMembers ? '?include_members=true' : '';
    return await apiRequest(`/admin/user-groups${query}`);
  },

  createUserGroup: async (groupData) => {
    return await apiRequest('/admin/user-groups', {
      method: 'POST',
      body: JSON.stringify(groupData)
    });
  },

  updateUserGroup: async (groupId, groupData) => {
    return await apiRequest(`/admin/user-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData)
    });
  },

  deleteUserGroup: async (groupId) => {
    return await apiRequest(`/admin/user-groups/${groupId}`, {
      method: 'DELETE'
    });
  },

  updateUserGroupMembers: async (groupId, payload) => {
    return await apiRequest(`/admin/user-groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  bulkUpdateUserGroupStatus: async (groupId, action) => {
    return await apiRequest(`/admin/user-groups/${groupId}/bulk-action`, {
      method: 'POST',
      body: JSON.stringify({ action })
    });
  },

  // Компании
  getCompanies: async (page = 1, limit = 20, search = '', isActive = null) => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    if (isActive !== null) params.append('is_active', isActive);
    return await apiRequest(`/admin/companies?${params}`);
  },

  getCompaniesStats: async () => {
    return await apiRequest('/admin/companies/stats');
  },

  getCompanyDetails: async (companyId) => {
    return await apiRequest(`/admin/companies/${companyId}`);
  },

  getCompanyAnalytics: async (companyId, { period = 'month', dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && dateFrom) params.append('date_from', dateFrom);
    if (period === 'custom' && dateTo) params.append('date_to', dateTo);
    return await apiRequest(`/admin/companies/${companyId}/analytics?${params}`);
  },

  exportCompanyAnalyticsToXlsx: async (companyId, { period = 'month', dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && dateFrom) params.append('date_from', dateFrom);
    if (period === 'custom' && dateTo) params.append('date_to', dateTo);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/admin/companies/${companyId}/analytics/export?${params}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'company_analytics.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return { success: true, filename, size: blob.size };
  },

  toggleCompanyActive: async (companyId) => {
    return await apiRequest(`/admin/companies/${companyId}/toggle-active`, {
      method: 'PUT'
    });
  },

  updateCompanySettings: async (companyId, settings) => {
    return await apiRequest(`/admin/companies/${companyId}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  addCompanyMembers: async (companyId, payload) => {
    return await apiRequest(`/admin/companies/${companyId}/members`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  deleteCompany: async (companyId) => {
    return await apiRequest(`/admin/companies/${companyId}`, {
      method: 'DELETE'
    });
  },

  changeCompanyOwner: async (companyId, newOwnerUserId) => {
    return await apiRequest(`/admin/companies/${companyId}/owner`, {
      method: 'PUT',
      body: JSON.stringify({ new_owner_user_id: newOwnerUserId })
    });
  },

  getCompanyOwnerCandidates: async (companyId, search = '', limit = 500, offset = 0) => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (search) params.append('search', search);
    return await apiRequest(
      `/admin/companies/${companyId}/owner-candidates?${params}`
    );
  },

  getCompanyAdminPublicTokenQuota: async (companyId) => {
    return await apiRequest(`/admin/companies/${companyId}/admin-public-token-quota`);
  },

  setCompanyAdminPublicTokenQuota: async (companyId, { tokens_limit, recurring, notes }) => {
    return await apiRequest(`/admin/companies/${companyId}/admin-public-token-quota`, {
      method: 'PUT',
      body: JSON.stringify({ tokens_limit, recurring, notes })
    });
  },

  deleteCompanyAdminPublicTokenQuota: async (companyId) => {
    return await apiRequest(`/admin/companies/${companyId}/admin-public-token-quota`, {
      method: 'DELETE'
    });
  },

  // Статистика
  getOverviewStats: async () => {
    return await apiRequest('/admin/stats/overview');
  },

  getRegistrationsStats: async (page = 1, limit = 5) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    return await apiRequest(`/admin/stats/registrations?${params}`);
  },

  exportRegistrationsToXlsx: async () => {
    const url = '/admin/stats/registrations/export';

    try {
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`/api/${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Проблема с авторизацией. Возможно, срок действия токена истек. Попробуйте обновить страницу и войти снова.');
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
      }

      if (!contentType || !contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        const responseText = await response.text();
        throw new Error(`Неверный тип контента: ${contentType}. Ответ: ${responseText.substring(0, 200)}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'registrations_export.xlsx';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Получен пустой файл');
      }

      if (blob.size < 1000) {
        throw new Error('Файл слишком маленький, возможно он поврежден');
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);

      return { success: true, filename, size: blob.size };
    } catch (error) {
      console.error('Ошибка при выгрузке регистраций:', error);
      throw error;
    }
  },

  getOnlineUsers: async (minutes = 15) => {
    return await apiRequest(`/admin/stats/online-users?minutes=${minutes}`);
  },

  getUsageStats: async (days = 30) => {
    return await apiRequest(`/admin/stats/usage?days=${days}`);
  },

  getUsersActivityStats: async (days = 30, comparePeriod = 'day', dateRange = null) => {
    const params = new URLSearchParams({
      days: days.toString(),
      compare_period: comparePeriod
    });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/users-activity?${params}`);
  },

  getAppTokenStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/app-tokens?${params}`);
  },

  getAppOperationsOverview: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/app-operations-overview?${params}`);
  },

  exportAppOperationsOverviewToXlsx: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/admin/stats/app-operations-export?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'app_operations_export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return { success: true, filename, size: blob.size };
  },

  getVksLaunchStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/vks-launches?${params}`);
  },

  getDesktopAgentReleases: async () => {
    return await apiRequest('/admin/desktop-agent/releases');
  },

  uploadDesktopAgentRelease: async ({ version, os_name, arch, notes, file }, onProgress) => {
    const bytesToBase64 = (bytes) => {
      let binary = '';
      const step = 0x8000;
      for (let index = 0; index < bytes.length; index += step) {
        binary += String.fromCharCode(...bytes.subarray(index, index + step));
      }
      return btoa(binary);
    };
    const started = await apiRequest('/admin/desktop-agent/releases/uploads', {
      method: 'POST',
      body: JSON.stringify({
        version,
        os_name,
        arch,
        notes: notes || '',
        original_filename: file?.name || 'onlanta.exe',
        total_size: file?.size || 0,
      }),
    });
    const uploadId = started.upload_id;
    const chunkSize = started.chunk_size;
    let sent = 0;
    for (let offset = 0; offset < file.size; offset += chunkSize) {
      const slice = file.slice(offset, Math.min(file.size, offset + chunkSize));
      const bytes = new Uint8Array(await slice.arrayBuffer());
      for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] ^= 0xa5;
      }
      let attempt = 0;
      while (attempt < 3) {
        try {
          await apiRequest(`/admin/desktop-agent/releases/uploads/${uploadId}/chunks`, {
            method: 'POST',
            body: JSON.stringify({
              offset,
              data: bytesToBase64(bytes),
            }),
            timeout: 120000,
            quietErrors: true,
          });
          break;
        } catch (error) {
          attempt += 1;
          if (attempt >= 3) throw error;
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
      sent += slice.size;
      if (onProgress) onProgress(sent, file.size);
    }
    return await apiRequest(`/admin/desktop-agent/releases/uploads/${uploadId}/complete`, {
      method: 'POST',
    });
  },

  activateDesktopAgentRelease: async (releaseId) => {
    return await apiRequest(`/admin/desktop-agent/releases/${releaseId}/activate`, {
      method: 'POST'
    });
  },

  deleteDesktopAgentRelease: async (releaseId) => {
    return await apiRequest(`/admin/desktop-agent/releases/${releaseId}`, {
      method: 'DELETE'
    });
  },

  getDesktopAgentPrompts: async () => {
    return await apiRequest('/admin/desktop-agent/prompts');
  },

  updateDesktopAgentPrompt: async (name, content) => {
    return await apiRequest(`/admin/desktop-agent/prompts/${encodeURIComponent(name)}`, {
      method: 'PUT',
      body: JSON.stringify({ content })
    });
  },

  getDesktopAgentStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/desktop-agent?${params}`);
  },

  getVidzheyLaunchStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/vidzhey-launches?${params}`);
  },

  getModelUsageStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/model-usage?${params}`);
  },

  exportModelUsageStats: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/model-usage-export?${params}`);
  },

  // Статистика AI кредитов
  getAppCreditsStats: async (days = 30) => {
    return await apiRequest(`/admin/stats/app-credits?days=${days}`);
  },

  // Детальная статистика по пользователям
  getUsersDetailedStats: async (days = 30, page = 1, limit = 20, search = '', dateRange = null) => {
    const params = new URLSearchParams({
      days: days.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    if (search) {
      params.append('search', search);
    }
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/users-detailed?${params}`);
  },

  getAdvertisingConsentUsers: async (page = 1, limit = 10) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    return await apiRequest(`/admin/stats/advertising-consent-users?${params}`);
  },

  // Статистика токенов публичных моделей по пользователям
  getPublicModelTokensStats: async (days = 30, page = 1, limit = 10, search = '', dateRange = null) => {
    const params = new URLSearchParams({
      days: days.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    if (search) {
      params.append('search', search);
    }
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/public-model-tokens?${params}`);
  },

  exportPublicModelTokensToXlsx: async (days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/admin/stats/public-model-tokens-export?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'public_model_tokens_export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return { success: true, filename, size: blob.size };
  },

  // Детальная статистика по конкретному пользователю
  getUserDetailedStats: async (userId, days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/user/${userId}/details?${params}`);
  },

  exportUserDetailedStatsToXlsx: async (userId, days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/admin/stats/user/${userId}/details-export?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'user_stats.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return { success: true, filename, size: blob.size };
  },

  getAppUsageDetails: async (appId, page = 1, limit = 50, days = 30, dateRange = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      days: days.toString()
    });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/app-usage-details/${appId}?${params}`);
  },

  exportAppUsageDetails: async (appId, days = 30, dateRange = null) => {
    const params = new URLSearchParams({ days: days.toString() });
    if (dateRange?.startDate && dateRange?.endDate) {
      params.append('start_date', dateRange.startDate);
      params.append('end_date', dateRange.endDate);
    }
    return await apiRequest(`/admin/stats/app-usage-export/${appId}?${params}`);
  },

  // Промпты
  getPrompts: async (type = '') => {
    const params = type ? `?prompt_type=${type}` : '';
    return await apiRequest(`/admin/prompts${params}`);
  },

  createPrompt: async (promptData) => {
    return await apiRequest('/admin/prompts', {
      method: 'POST',
      body: JSON.stringify(promptData)
    });
  },

  updatePrompt: async (promptId, promptData) => {
    return await apiRequest(`/admin/prompts/${promptId}`, {
      method: 'PUT',
      body: JSON.stringify(promptData)
    });
  },

  deletePrompt: async (promptId) => {
    return await apiRequest(`/admin/prompts/${promptId}`, {
      method: 'DELETE'
    });
  },

  // Контент лендинга
  getContent: async () => {
    return await apiRequest('/admin/content');
  },

  updateContent: async (section, contentData) => {
    return await apiRequest(`/admin/content/${section}`, {
      method: 'PUT',
      body: JSON.stringify(contentData)
    });
  },

  createContent: async (contentData) => {
    return await apiRequest('/admin/content', {
      method: 'POST',
      body: JSON.stringify(contentData)
    });
  },

  deleteContent: async (section) => {
    return await apiRequest(`/admin/content/${section}`, {
      method: 'DELETE'
    });
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return await apiRequest('/admin/upload-image', {
      method: 'POST',
      body: formData,
      // Не устанавливаем Content-Type, браузер сам установит multipart/form-data
      headers: {}
    });
  },

  reorderContent: async (items) => {
    return await apiRequest('/admin/content/reorder', {
      method: 'PUT',
      body: JSON.stringify(items)
    });
  },

  getKnowledgeBaseArticles: async () => {
    return await apiRequest('/admin/knowledge-base/articles');
  },

  uploadKnowledgeBaseImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest('/admin/knowledge-base/upload-image', {
      method: 'POST',
      body: formData,
      headers: {}
    });
  },

  createKnowledgeBaseArticle: async (articleData) => {
    return await apiRequest('/admin/knowledge-base/articles', {
      method: 'POST',
      body: JSON.stringify(articleData)
    });
  },

  updateKnowledgeBaseArticle: async (articleId, articleData) => {
    return await apiRequest(`/admin/knowledge-base/articles/${articleId}`, {
      method: 'PUT',
      body: JSON.stringify(articleData)
    });
  },

  deleteKnowledgeBaseArticle: async (articleId) => {
    return await apiRequest(`/admin/knowledge-base/articles/${articleId}`, {
      method: 'DELETE'
    });
  },

  exportKnowledgeBaseArticles: async () => {
    const downloadArchive = async (token = localStorage.getItem('accessToken')) => (
      await fetch('/api/admin/knowledge-base/export', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
    );

    let response = await downloadArchive();

    if (response.status === 401) {
      const newToken = await refreshAuthToken();
      if (newToken) {
        response = await downloadArchive(newToken);
      }
    }

    if (!response.ok) {
      let errorMessage = 'Ошибка экспорта базы знаний';
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch (parseError) {
        console.warn('Не удалось прочитать ошибку экспорта базы знаний:', parseError);
      }
      throw new Error(errorMessage);
    }

    return await response.blob();
  },

  importKnowledgeBaseArticles: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return await apiRequest('/admin/knowledge-base/import', {
      method: 'POST',
      body: formData,
      headers: {}
    });
  },

  // RAG v2 управление
  getRagStats: async () => {
    return await apiRequest('/admin/rag-v2/stats');
  },

  reindexRag: async (userId = null, updateChromadb = false) => {
    let url = '/admin/rag-v2/reindex';
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    if (updateChromadb) params.append('update_chromadb', 'true');
    if (params.toString()) url += `?${params.toString()}`;
    
    return await apiRequest(url, {
      method: 'POST'
    });
  },

  // 🧹 Очистка осиротевших данных
  cleanupOrphanedData: async (userId = null) => {
    const url = userId 
      ? `/admin/rag-v2/cleanup-orphaned?user_id=${userId}` 
      : '/admin/rag-v2/cleanup-orphaned';
    return await apiRequest(url, {
      method: 'POST'
    });
  },

  // 🏥 Проверка здоровья RAG v2
  ragHealthCheck: async () => {
    return await apiRequest('/admin/rag-v2/health-check');
  },

  // 🔄 Синхронизация папок в Neo4j
  syncFoldersToNeo4j: async () => {
    return await apiRequest('/rag-v2/admin/sync-folders-to-neo4j', {
      method: 'POST'
    });
  },

  // 📊 Статус реранкера
  getRerankerStatus: async () => {
    return await apiRequest('/rag-v2/admin/reranker-status');
  },

  // 🚀 Инициализация реранкера
  initializeReranker: async () => {
    return await apiRequest('/rag-v2/admin/reranker-initialize', {
      method: 'POST'
    });
  },

  // ==================== MILVUS MIGRATION FUNCTIONS ====================

  // 🚀 Миграция на Milvus (длительная операция - таймаут 2 часа)
  migrateToMilvus: async (userId = null, batchSize = 100, recalculateEmbeddings = false) => {
    const params = new URLSearchParams();
    if (userId) params.append('user_id', userId);
    params.append('batch_size', batchSize);
    if (recalculateEmbeddings) params.append('recalculate_embeddings', 'true');
    
    return await apiRequest(`/admin/rag-v2/migrate-to-milvus?${params}`, {
      method: 'POST',
      timeout: 7200000 // 2 часа для миграции с пересчётом embeddings
    });
  },

  // 📊 Статус миграции
  getMigrationStatus: async () => {
    return await apiRequest('/admin/rag-v2/migration-status');
  },

  // 🔙 Откат миграции
  rollbackMigration: async (userId = null) => {
    const params = userId ? `?user_id=${userId}` : '';
    return await apiRequest(`/admin/rag-v2/rollback-migration${params}`, {
      method: 'POST'
    });
  },

  // 🔍 Верификация миграции
  verifyMigration: async (userId, sampleSize = 10) => {
    return await apiRequest(`/admin/rag-v2/verify-migration?user_id=${userId}&sample_size=${sampleSize}`, {
      method: 'POST'
    });
  },

  // 🏥 Здоровье векторных хранилищ
  getVectorStoresHealth: async () => {
    return await apiRequest('/admin/rag-v2/vector-stores-health');
  },

  // ==================== EMAIL FUNCTIONS ====================
  
  // Получение настроек email
  getEmailSettings: async () => {
    return await apiRequest('/admin/email/settings');
  },

  // Обновление SMTP настроек email
  updateEmailSettings: async (data) => {
    return await apiRequest('/admin/email/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Тестирование подключения к SMTP
  testEmailConnection: async () => {
    return await apiRequest('/admin/email/test-connection', {
      method: 'POST'
    });
  },

  // Отправка тестового email
  sendTestEmail: async (data) => {
    return await apiRequest('/admin/email/send-test', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Получение списка пользователей для рассылки
  getEmailUsersList: async (activeOnly = true) => {
    return await apiRequest(`/admin/email/users-list?active_only=${activeOnly}`);
  },

  // Массовая рассылка
  sendBulkEmail: async (data) => {
    return await apiRequest('/admin/email/send-bulk', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Рассылка всем пользователям
  sendToAllUsers: async (subject, message, activeOnly = true) => {
    const params = new URLSearchParams({
      subject: subject,
      message: message,
      active_only: activeOnly
    });
    return await apiRequest(`/admin/email/send-to-all-users?${params}`, {
      method: 'POST'
    });
  },

  // Массовая отправка внутренних уведомлений
  broadcastNotification: async (data) => {
    return await apiRequest('/admin/notifications/broadcast', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // ==================== BLOCKED EMAIL DOMAINS ====================
  
  // Получение списка заблокированных доменов
  getBlockedDomains: async (page = 1, limit = 50, search = '', isActive = null) => {
    const params = new URLSearchParams({ page, limit });
    if (search) params.append('search', search);
    if (isActive !== null) params.append('is_active', isActive);
    return await apiRequest(`/admin/email/blocked-domains?${params}`);
  },

  // Добавление домена в черный список
  addBlockedDomain: async (domain, description = null) => {
    return await apiRequest('/admin/email/blocked-domains', {
      method: 'POST',
      body: JSON.stringify({ domain, description })
    });
  },

  // Массовое добавление доменов
  addBlockedDomainsBulk: async (domains, description = null) => {
    return await apiRequest('/admin/email/blocked-domains/bulk', {
      method: 'POST',
      body: JSON.stringify({ domains, description })
    });
  },

  // Обновление домена
  updateBlockedDomain: async (domainId, data) => {
    return await apiRequest(`/admin/email/blocked-domains/${domainId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Переключение статуса домена
  toggleBlockedDomainActive: async (domainId) => {
    return await apiRequest(`/admin/email/blocked-domains/${domainId}/toggle-active`, {
      method: 'PUT'
    });
  },

  // Удаление домена
  deleteBlockedDomain: async (domainId) => {
    return await apiRequest(`/admin/email/blocked-domains/${domainId}`, {
      method: 'DELETE'
    });
  },

  // Статистика по заблокированным доменам
  getBlockedDomainsStats: async () => {
    return await apiRequest('/admin/email/blocked-domains/stats');
  },

  // ==================== REGISTRATION OFFERS ====================

  getRegistrationOffers: async () => {
    return await apiRequest('/admin/registration-offers/');
  },

  getRegistrationOfferStats: async (offerId, period = 'month') => {
    return await apiRequest(`/admin/registration-offers/${offerId}/stats?period=${encodeURIComponent(period)}`);
  },

  exportRegistrationOfferStats: async (offerId, period = 'month') => {
    const response = await fetch(
      `/api/admin/registration-offers/${offerId}/stats/export?period=${encodeURIComponent(period)}`,
      {
        method: 'GET',
        headers: getAuthHeaders()
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Ошибка выгрузки статистики оффера');
    }

    return await response.blob();
  },

  createRegistrationOffer: async (data) => {
    return await apiRequest('/admin/registration-offers/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateRegistrationOffer: async (offerId, data) => {
    return await apiRequest(`/admin/registration-offers/${offerId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteRegistrationOffer: async (offerId) => {
    return await apiRequest(`/admin/registration-offers/${offerId}`, {
      method: 'DELETE'
    });
  },

  // ==================== AI APPLICATIONS MANAGEMENT ====================
  
  // Получение списка всех AI приложений
  getAIApplications: async () => {
    return await apiRequest('/admin/ai-applications');
  },

  // Получение конкретного AI приложения
  getAIApplication: async (appId) => {
    return await apiRequest(`/admin/ai-applications/${appId}`);
  },

  // Обновление AI приложения
  updateAIApplication: async (appId, data) => {
    return await apiRequest(`/admin/ai-applications/${appId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Переключение активности AI приложения
  toggleAIApplicationActive: async (appId) => {
    return await apiRequest(`/admin/ai-applications/${appId}/toggle-active`, {
      method: 'PUT'
    });
  },

  // Переключение статуса "Новое" для AI приложения
  toggleAIApplicationNew: async (appId) => {
    return await apiRequest(`/admin/ai-applications/${appId}/toggle-new`, {
      method: 'PUT'
    });
  },

  // ==================== ГРУППЫ AI ПРИЛОЖЕНИЙ ====================

  getAIAppGroups: async () => {
    return await apiRequest('/admin/ai-app-groups');
  },

  createAIAppGroup: async (data) => {
    return await apiRequest('/admin/ai-app-groups', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateAIAppGroup: async (groupId, data) => {
    return await apiRequest(`/admin/ai-app-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteAIAppGroup: async (groupId) => {
    return await apiRequest(`/admin/ai-app-groups/${groupId}`, {
      method: 'DELETE'
    });
  },

  // ==================== КАПЧА (Altcha) ====================

  getCaptchaSites: async () => {
    return await apiRequest('/admin/captcha-sites');
  },

  createCaptchaSite: async (data) => {
    return await apiRequest('/admin/captcha-sites', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateCaptchaSite: async (siteId, data) => {
    return await apiRequest(`/admin/captcha-sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteCaptchaSite: async (siteId) => {
    return await apiRequest(`/admin/captcha-sites/${siteId}`, {
      method: 'DELETE'
    });
  },

  rotateCaptchaSiteSecret: async (siteId) => {
    return await apiRequest(`/admin/captcha-sites/${siteId}/rotate-secret`, {
      method: 'POST'
    });
  },

  getCaptchaSiteStats: async (siteId, days = 7) => {
    return await apiRequest(`/admin/captcha-sites/${siteId}/stats?days=${days}`);
  },

  // Получить статистику капчи
  getCaptchaStats: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 50,
      ...(params.form_type && { form_type: params.form_type }),
      ...(params.verified !== undefined && { verified: params.verified }),
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date })
    }).toString();

    return await apiRequest(`/admin/captcha-stats?${queryParams}`);
  },

  // Получить сводку статистики капчи
  getCaptchaSummary: async (days = 7) => {
    return await apiRequest(`/admin/captcha-stats/summary?days=${days}`);
  },

  // Очистить статистику капчи
  clearCaptchaStats: async (days = null) => {
    const params = days ? `?days=${days}` : '';
    return await apiRequest(`/admin/captcha-stats${params}`, {
      method: 'DELETE'
    });
  }
};

// API методы для Смартизатора
export const smartizerAPI = {
  // Управление чатами
  getChats: async () => {
    return await apiRequest('/smartizer/chats');
  },

  createChat: async (chatData) => {
    return await apiRequest('/smartizer/chats', {
      method: 'POST',
      body: JSON.stringify(chatData)
    });
  },

  updateChat: async (chatId, chatData) => {
    return await apiRequest(`/smartizer/chats/${chatId}`, {
      method: 'PUT',
      body: JSON.stringify(chatData)
    });
  },

  deleteChat: async (chatId) => {
    return await apiRequest(`/smartizer/chats/${chatId}`, {
      method: 'DELETE'
    });
  },

  // Сообщения
  getChatMessages: async (chatId) => {
    const chat = await apiRequest(`/smartizer/chats/${chatId}`);
    return chat.messages || [];
  },

  sendMessage: async (chatId, messageData) => {
    return await apiRequest(`/smartizer/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  }
};

// API методы для HR агента
export const hrAgentAPI = {
  // =============== ПРОФИЛИ ВАКАНСИЙ ===============
  
  // Управление профилями вакансий
  getJobProfiles: async (page = 1, limit = 20, status = null) => {
    const statusParam = status ? `&status=${status}` : '';
    return await apiRequest(`/hr-agent/job-profiles?page=${page}&limit=${limit}${statusParam}`);
  },

  getJobProfile: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}`);
  },

  createJobProfile: async (profileData) => {
    return await apiRequest('/hr-agent/job-profiles', {
      method: 'POST',
      body: JSON.stringify(profileData)
    });
  },

  updateJobProfileStatus: async (profileId, status) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  },

  // =============== БРИФ-ДИАЛОГ ===============

  getBriefState: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief`);
  },

  startBrief: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/start`, {
      method: 'POST'
    });
  },

  saveBriefAnswer: async (profileId, questionId, answer) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/answer`, {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId, answer })
    });
  },

  briefFromTranscript: async (profileId, text) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/transcript`, {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  },

  briefFromAudio: async (profileId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/audio`, {
      method: 'POST',
      headers: {},
      body: formData
    });
  },

  completeBrief: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/complete`, {
      method: 'POST'
    });
  },

  confirmBrief: async (profileId, profileData = null) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/brief/confirm`, {
      method: 'POST',
      body: JSON.stringify({ profile_data: profileData })
    });
  },

  getBriefSettings: async () => {
    return await apiRequest('/hr-agent/brief-settings');
  },

  saveBriefSettings: async (payload) => {
    return await apiRequest('/hr-agent/brief-settings', {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  extractBriefQuestionsFromText: async (text) => {
    return await apiRequest('/hr-agent/brief-settings/extract-text', {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  },

  extractBriefQuestionsFromAudio: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest('/hr-agent/brief-settings/extract-audio', {
      method: 'POST',
      headers: {},
      body: formData
    });
  },

  // =============== ЗАРПЛАТНЫЙ ПОИСК ===============

  runSalaryResearch: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/salary-research`, {
      method: 'POST'
    });
  },

  runVacancySearch: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/vacancy-search`, {
      method: 'POST'
    });
  },

  runResumeSearch: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/resume-search`, {
      method: 'POST'
    });
  },

  // =============== СТАТУСЫ КАНДИДАТОВ ===============

  updateCandidateStatus: async (sessionId, status) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/candidate-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  },

  // =============== СОБЕСЕДОВАНИЯ ===============

  uploadInterviewRecording: async (sessionId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest(`/hr-agent/sessions/${sessionId}/interview-upload`, {
      method: 'POST',
      headers: {},
      body: formData
    });
  },

  setInterviewTranscript: async (sessionId, text) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/interview-transcript`, {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  },

  analyzeInterview: async (sessionId) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/interview-analyze`, {
      method: 'POST'
    });
  },

  getInterviewDetails: async (sessionId) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/interview`);
  },

  updateJobProfile: async (profileId, profileData) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}`, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  },

  deleteJobProfile: async (profileId) => {
    return await apiRequest(`/hr-agent/job-profiles/${profileId}`, {
      method: 'DELETE'
    });
  },

  // Рейтинг кандидатов по профилю
  getJobProfileRanking: async (profileId, page = 1, limit = 10, favoriteOnly = false, candidateStatus = null) => {
    const statusParam = candidateStatus ? `&candidate_status=${candidateStatus}` : '';
    return await apiRequest(`/hr-agent/job-profiles/${profileId}/ranking?page=${page}&limit=${limit}&favorite_only=${favoriteOnly}${statusParam}`);
  },

  // =============== РЕЗЮМЕ ===============

  // Управление сессиями
  getSessions: async (page = 1, limit = 20) => {
    return await apiRequest(`/hr-agent/sessions?page=${page}&limit=${limit}`);
  },

  getSessionDetails: async (sessionId) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}`);
  },

  deleteSession: async (sessionId) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  },

  // Удаление резюме из профиля вакансии
  deleteResumeFromProfile: async (sessionId, profileId) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/from-profile/${profileId}`, {
      method: 'DELETE'
    });
  },

  updateSessionFavorite: async (sessionId, isFavorite) => {
    return await apiRequest(`/hr-agent/sessions/${sessionId}/favorite`, {
      method: 'PATCH',
      body: JSON.stringify({ is_favorite: isFavorite })
    });
  },

  // Загрузка и анализ резюме
  uploadResume: async (file, title = null, jobProfileId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) {
      formData.append('title', title);
    }
    if (jobProfileId) {
      formData.append('job_profile_id', jobProfileId);
    }

    return await apiRequest('/hr-agent/upload-resume', {
      method: 'POST',
      headers: {},
      body: formData
    });
  },

  // Массовая загрузка резюме
  bulkUploadResumes: async (files, jobProfileId) => {
    const formData = new FormData();
    
    // Добавляем файлы
    files.forEach(file => {
      formData.append('files', file);
    });
    
    formData.append('job_profile_id', jobProfileId);
    // Автоанализ всегда включен на бэкенде

    return await apiRequest('/hr-agent/bulk-upload-resumes', {
      method: 'POST',
      headers: {},
      body: formData
    });
  },

  analyzeResume: async (sessionId) => {
    return await apiRequest(`/hr-agent/analyze/${sessionId}`, {
      method: 'POST'
    });
  },

  // Чат по резюме
  sendChatMessage: async (sessionId, content) => {
    return await apiRequest(`/hr-agent/chat/${sessionId}`, {
      method: 'POST',
      body: JSON.stringify({ content })
    });
  },

  // Статистика
  getStats: async () => {
    return await apiRequest('/hr-agent/stats');
  }
};

// API методы для RAG системы
export const ragAPI = {
  uploadDocument: async (file, userId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);

    return await apiRequest('/rag/upload', {
      method: 'POST',
      headers: {}, // Убираем Content-Type для FormData
      body: formData
    });
  },

  getDocuments: async (page = 1, limit = 20) => {
    return await apiRequest(`/rag/documents?page=${page}&limit=${limit}`);
  },

  deleteDocument: async (documentId) => {
    return await apiRequest(`/rag/documents/${documentId}`, {
      method: 'DELETE'
    });
  },

  getChats: async () => {
    return await apiRequest('/rag/chats');
  },

  createChat: async (title) => {
    return await apiRequest('/rag/chats', {
      method: 'POST',
      body: JSON.stringify({ title })
    });
  },

  deleteChat: async (chatId) => {
    return await apiRequest(`/rag/chats/${chatId}`, {
      method: 'DELETE'
    });
  },

  getChatMessages: async (chatId) => {
    return await apiRequest(`/rag/chats/${chatId}/messages`);
  },

  sendMessage: async (chatId, query) => {
    return await apiRequest(`/rag/chats/${chatId}/message`, {
      method: 'POST',
      body: JSON.stringify({ query })
    });
  },

  getStats: async () => {
    return await apiRequest('/rag/stats');
  }
};

export default {
  composeProtocolHtml,
  authAPI,
  transcriptionAPI,
  transcriptionShareAPI,
  textAPI,
  billingAPI,
  apiKeysAPI,
  adminAPI,
  smartizerAPI,
  hrAgentAPI,
  ragAPI,
  imageGenerationAPI,
  companyAPI
}; 

// Функция для тестирования соединения и диагностики проблем
export const testConnection = async ({ includeOllama = true } = {}) => {
  //.env.REACT_APP_API_URL || '/api';
  const results = {
    health: { status: 'pending', message: '' },
    auth: { status: 'pending', message: '' },
    ollama: { status: 'pending', message: '' }
  };

  try {
    // Тест 1: Проверка health endpoint
    const healthResponse = await fetchWithTimeout(`/api/health`, { method: 'HEAD' }, 5000);
    
    if (healthResponse.ok) {
      results.health = { status: 'success', message: 'Сервер доступен' };
    } else {
      results.health = { status: 'error', message: `Сервер недоступен: ${healthResponse.status}` };
    }
  } catch (error) {
    results.health = { status: 'error', message: `Ошибка соединения: ${error.message}` };
  }

  try {
    // Тест 2: Проверка авторизации (если токен есть)
    const token = localStorage.getItem('accessToken');
    if (token) {
      // Используем прямой вызов API вместо authAPI для избежания циклических зависимостей
      const authResponse = await fetchWithTimeout(`/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }, 5000);
      
      if (authResponse.ok) {
        results.auth = { status: 'success', message: 'Авторизация работает' };
      } else {
        results.auth = { status: 'error', message: `Ошибка авторизации: ${authResponse.status}` };
      }
    } else {
      results.auth = { status: 'warning', message: 'Нет токена авторизации' };
    }
  } catch (error) {
    results.auth = { status: 'error', message: `Ошибка авторизации: ${error.message}` };
  }

  if (includeOllama) {
    try {
      // Тест 3: Проверка Ollama API
      const ollamaResponse = await textAPI.checkOllama();
      if (ollamaResponse.ollama_available) {
        results.ollama = { status: 'success', message: `Ollama доступна, модель: ${ollamaResponse.model}` };
      } else {
        results.ollama = { status: 'error', message: 'Ollama недоступна' };
      }
    } catch (error) {
      results.ollama = { status: 'error', message: `Ошибка Ollama: ${error.message}` };
    }
  } else {
    results.ollama = { status: 'skipped', message: 'Проверка Ollama пропущена' };
  }

  return results;
};

// Экспорт функции для быстрых API запросов с таймаутом
export { quickApiRequest };

// API для системы голосований за AI приложения
export const aiAppVotesAPI = {
  // Получить список активных приложений для меню
  getMenuApps: async () => {
    return await apiRequest('/ai-apps/menu/list');
  },

  // Получить информацию о конкретном приложении
  getAppInfo: async (appId) => {
    return await apiRequest(`/ai-apps/${appId}/info`);
  },

  // Получить ТОП N приложений по лайкам
  getTopApps: async (limit = 5) => {
    try {
      // Сначала пытаемся получить с аутентификацией (если пользователь авторизован)
      const token = localStorage.getItem('accessToken');
      if (token) {
        return await apiRequest(`ai-apps/top?limit=${limit}`);
      } else {
        // Если нет токена, используем публичный эндпоинт
        return await fetch(`/api/ai-apps/top/public?limit=${limit}`)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          });
      }
    } catch (error) {
      console.error('Ошибка получения ТОП приложений:', error);
      // Fallback на публичный эндпоинт в случае ошибки
      return await fetch(`/api/ai-apps/top/public?limit=${limit}`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        });
    }
  },

  // Получить список AI приложений с статистикой
  getAppsWithStats: async () => {
    return await apiRequest('/ai-apps/');
  },

  // Записать открытие AI приложения
  recordUsage: async (appId, source = 'manual') => {
    return await apiRequest('/ai-apps/usage', {
      method: 'POST',
      body: JSON.stringify({
        app_id: appId,
        source
      }),
      quietErrors: true
    });
  },

  // Получить персональную рекомендацию приложения
  getRecommendation: async () => {
    return await apiRequest('/ai-apps/recommendation', {
      quietErrors: true
    });
  },

  // Отметить, что модальное окно рекомендации уже показано
  markRecommendationModalShown: async () => {
    return await apiRequest('/ai-apps/recommendation/modal-shown', {
      method: 'POST',
      quietErrors: true
    });
  },

  // Проголосовать за AI приложение
  vote: async (appId, voteType) => {
    return await apiRequest('/ai-apps/vote', {
      method: 'POST',
      body: JSON.stringify({
        app_id: appId,
        vote_type: voteType
      })
    });
  },

  // Получить административную статистику голосований (только для админов)
  getAdminStats: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 50,
      ...(params.app_id && { app_id: params.app_id }),
      ...(params.vote_type && { vote_type: params.vote_type }),
      ...(params.user_email && { user_email: params.user_email })
    }).toString();

    return await apiRequest(`/ai-apps/admin/votes?${queryParams}`);
  },

  // Получить документацию API для всех приложений
  getApiDocs: async () => {
    return await apiRequest('/api-docs/applications');
  }
};

// API для рискового анализа контрагентов
export const riskAnalysisAPI = {
  // Создать новую задачу рискового анализа
  createTask: async (taskData) => {
    return await apiRequest('/risk-analysis/', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  },

  // Получить список задач пользователя
  getTasks: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
      ...(params.status && { status: params.status })
    }).toString();

    return await apiRequest(`/risk-analysis/?${queryParams}`);
  },

  // Получить конкретную задачу
  getTask: async (taskId) => {
    return await apiRequest(`/risk-analysis/${taskId}`);
  },

  // Удалить задачу
  deleteTask: async (taskId) => {
    return await apiRequest(`/risk-analysis/${taskId}`, {
      method: 'DELETE'
    });
  },

  // Скачать отчет в формате DOCX
  downloadReport: async (taskId, companyName) => {
    try {
      const response = await fetch(`/api/risk-analysis/${taskId}/download`, {
        method: 'GET',
        headers: {
          ...getAuthHeaders()
        }
      });

      if (response.status === 401) {
        const newToken = await refreshAuthToken();
        if (newToken) {
          return riskAnalysisAPI.downloadReport(taskId, companyName);
        }
        throw new Error('Сессия истекла, требуется повторная авторизация');
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка скачивания: ${errorText}`);
      }

      // Создаем blob и скачиваем файл
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `risk_analysis_${companyName}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error) {
      console.error('Ошибка скачивания отчета:', error);
      throw error;
    }
  },

  // Получить статистику рискового анализа
  getStats: async () => {
    return await apiRequest('/risk-analysis/stats/overview');
  }
};

// API для AI-Аналитика (веб-исследования)
export const webAnalystAPI = {
  start: async (payload) => {
    return await apiRequest('/web-analyst/start', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },
  status: async (taskId) => {
    return await apiRequest(`/web-analyst/status/${taskId}`);
  },
  download: async (taskId) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`/api/web-analyst/download/${taskId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Ошибка скачивания');
    return res;
  }
};

// API для приложения Контент Завод
export const contentFactoryAPI = {
  // Создать новую задачу генерации контента
  createTask: async (taskData) => {
    return await apiRequest('/content-factory/', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  },

  // Получить список задач пользователя
  getTasks: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      size: params.size || 20,
      ...(params.status && { status: params.status })
    }).toString();

    return await apiRequest(`/content-factory/?${queryParams}`);
  },

  // Получить конкретную задачу
  getTask: async (taskId) => {
    return await apiRequest(`/content-factory/${taskId}`);
  },

  // Удалить задачу
  deleteTask: async (taskId) => {
    return await apiRequest(`/content-factory/${taskId}`, {
      method: 'DELETE'
    });
  },

  // Получить статистику контент-завода
  getStats: async () => {
    return await apiRequest('/content-factory/stats/overview');
  },

  // Предварительный просмотр контента
  previewContent: async (previewData) => {
    return await apiRequest('/content-factory/preview', {
      method: 'POST',
      body: JSON.stringify(previewData)
    });
  }
};

// API для Legal Assistant V2 - Управление правовой базой знаний
export const legalV2API = {
  // Создание рискового заключения по контрагенту (без файла)
  createRiskAnalysis: async (riskData) => {
    return await apiRequest('/legal-assistant/risk-analysis', {
      method: 'POST',
      body: JSON.stringify(riskData)
    });
  },

  // Админ-панель: Управление законами
  uploadLaw: async (formData) => {
    try {
      const response = await fetch('/api/legal-v2/knowledge/laws/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData // FormData сам установит Content-Type
      });

      // Сначала проверяем статус ответа
      if (!response.ok) {
        // Пытаемся получить детали ошибки из JSON, если возможно
        let errorMessage = 'Ошибка загрузки закона';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Если не JSON, используем статус
          errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Теперь парсим успешный ответ
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading law:', error);
      throw error;
    }
  },

  getLaws: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      page_size: params.page_size || 50,
      ...(params.is_active !== undefined && { is_active: params.is_active }),
      ...(params.law_type && { law_type: params.law_type })
    }).toString();

    return await apiRequest(`/legal-v2/knowledge/laws?${queryParams}`);
  },

  getLaw: async (lawId) => {
    return await apiRequest(`/legal-v2/knowledge/laws/${lawId}`);
  },

  deleteLaw: async (lawId) => {
    return await apiRequest(`/legal-v2/knowledge/laws/${lawId}`, {
      method: 'DELETE'
    });
  },

  getStats: async () => {
    return await apiRequest('/legal-v2/knowledge/stats');
  },

  // Анализ документов
  uploadDocument: async (formData) => {
    try {
      const originalFiles = formData.getAll('files');
      const originalFilesCount = originalFiles.length;

      let response = await fetch('/api/legal-v2/analysis/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const buildFormDataWithFiles = (sourceFormData) => {
        const fd = new FormData();
        const singleFile = sourceFormData.get('file');
        const files = sourceFormData.getAll('files');
        const effectiveFiles = files.length ? files : (singleFile ? [singleFile] : []);
        effectiveFiles.forEach((f) => fd.append('files', f));

        // Для совместимости с валидаторами, которые не принимают одиночный элемент как list.
        if (effectiveFiles.length === 1) {
          fd.append('files', effectiveFiles[0]);
        }

        if (singleFile) fd.append('file', singleFile);
        if (sourceFormData.get('title')) fd.append('title', sourceFormData.get('title'));
        if (sourceFormData.get('document_type')) fd.append('document_type', sourceFormData.get('document_type'));
        if (sourceFormData.get('main_document_filename')) {
          fd.append('main_document_filename', sourceFormData.get('main_document_filename'));
        }
        if (sourceFormData.get('selected_role')) {
          fd.append('selected_role', sourceFormData.get('selected_role'));
        }
        return fd;
      };

      const buildFormDataFileOnly = (sourceFormData) => {
        const fd = new FormData();
        const singleFile = sourceFormData.get('file') || sourceFormData.getAll('files')[0];
        if (singleFile) fd.append('file', singleFile);
        if (sourceFormData.get('title')) fd.append('title', sourceFormData.get('title'));
        if (sourceFormData.get('document_type')) fd.append('document_type', sourceFormData.get('document_type'));
        if (sourceFormData.get('main_document_filename')) {
          fd.append('main_document_filename', sourceFormData.get('main_document_filename'));
        }
        if (sourceFormData.get('selected_role')) {
          fd.append('selected_role', sourceFormData.get('selected_role'));
        }
        return fd;
      };

      // Совместимость между версиями API:
      // - для MULTI никогда не деградируем до file-only (иначе потеряем приложения)
      // - для SINGLE можем пробовать list/file варианты
      if (!response.ok && response.status === 422) {
        try {
          const cloned = response.clone();
          const errorData = await cloned.json();
          const detailText = JSON.stringify(errorData?.detail || '').toLowerCase();
          const files = formData.getAll('files');
          const hasFile = !!formData.get('file');
          const hasFiles = files.length > 0;

          const isListValidationError = detailText.includes('valid list');
          const isFileFieldError = detailText.includes('"file"') || detailText.includes('field required');

          // MULTI: только повтор с list-форматом, без перехода на file-only
          if (originalFilesCount > 1 && isListValidationError && hasFile) {
            const fallbackFormData = buildFormDataWithFiles(formData);
            response = await fetch('/api/legal-v2/analysis/upload', {
              method: 'POST',
              headers: getAuthHeaders(),
              body: fallbackFormData
            });
          } else if (originalFilesCount <= 1 && isListValidationError && hasFile) {
            // SINGLE: сервер просит list
            const fallbackFormData = buildFormDataWithFiles(formData);
            response = await fetch('/api/legal-v2/analysis/upload', {
              method: 'POST',
              headers: getAuthHeaders(),
              body: fallbackFormData
            });
          } else if (!response.ok && response.status === 422 && originalFilesCount <= 1 && isFileFieldError && hasFiles) {
            // SINGLE: сервер просит одиночный file
            const fallbackFormData = buildFormDataFileOnly(formData);
            response = await fetch('/api/legal-v2/analysis/upload', {
              method: 'POST',
              headers: getAuthHeaders(),
              body: fallbackFormData
            });
          }
        } catch (e) {
          // Игнорируем и оставляем базовую обработку ошибки ниже
        }
      }

      // Сначала проверяем статус ответа
      if (!response.ok) {
        // Пытаемся получить детали ошибки из JSON, если возможно
        let errorMessage = 'Ошибка загрузки документа';
        try {
          const errorData = await response.json();
          if (Array.isArray(errorData?.detail)) {
            errorMessage = errorData.detail
              .map((item) => {
                const loc = Array.isArray(item?.loc) ? item.loc.join('.') : '';
                return `${loc ? `[${loc}] ` : ''}${item?.msg || JSON.stringify(item)}`;
              })
              .join('; ');
          } else if (typeof errorData?.detail === 'object' && errorData?.detail !== null) {
            errorMessage = JSON.stringify(errorData.detail);
          } else {
            errorMessage = errorData?.detail || errorMessage;
          }
        } catch (e) {
          // Если не JSON, используем статус
          errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Теперь парсим успешный ответ
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  getTask: async (taskId) => {
    return await apiRequest(`/legal-v2/analysis/tasks/${taskId}`);
  },

  getTaskGraph: async (taskId) => {
    return await apiRequest(`/legal-v2/analysis/tasks/${taskId}/graph`);
  },

  deleteTask: async (taskId) => {
    return await apiRequest(`/legal-v2/analysis/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  // История обработок документов с фильтрацией по типу
  getHistory: async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      page_size: params.page_size || 20,
      ...(params.document_type && { document_type: params.document_type }),
      ...(params.status && { status: params.status })
    }).toString();

    return await apiRequest(`/legal-v2/analysis/history?${queryParams}`);
  }
};

// API для DeepLog - анализа логов
export const deepLogAPI = {
  // Загрузка файла логов
  uploadLogFile: async (formData) => {
    try {
      // Используем uploadFileRequest с автоматической обработкой 401 и обновлением токена
      return await uploadFileRequest('log-analysis/upload', formData);
    } catch (error) {
      console.error('Ошибка загрузки файла логов:', error);
      throw error;
    }
  },

  // Получение списка задач
  getTasks: async (page = 1, pageSize = 20, statusFilter = null, tagIds = []) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (statusFilter) {
      params.append('status_filter', statusFilter);
    }
    
    tagIds.forEach((tagId) => params.append('tag_ids', tagId));

    return await apiRequest(`/log-analysis/tasks?${params.toString()}`);
  },

  // Получение личных тегов
  getTags: async () => {
    return await apiRequest('/log-analysis/tags');
  },

  // Получение конкретной задачи
  getTask: async (taskId) => {
    return await apiRequest(`/log-analysis/tasks/${taskId}`);
  },

  // Обновление тегов задачи
  updateTaskTags: async (taskId, tags) => {
    return await apiRequest(`/log-analysis/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify({ tags })
    });
  },

  // Удаление задачи
  deleteTask: async (taskId) => {
    return await apiRequest(`/log-analysis/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  // Получение статистики
  getStats: async () => {
    return await apiRequest('/log-analysis/stats');
  },

  // Скачивание PDF отчета
  downloadPDF: async (taskId) => {
    const downloadWithToken = async (token = null) => {
      const headers = token 
        ? { 'Authorization': `Bearer ${token}` }
        : getAuthHeaders();
        
      const response = await fetch(`/api/log-analysis/tasks/${taskId}/download-pdf`, {
        method: 'GET',
        headers: headers
      });
      
      // Обработка истекшего токена
      if (response.status === 401 && !token) {
        console.log('🔐 Токен истек при скачивании PDF, обновляем...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          console.log('✅ Токен обновлен, повторяем скачивание...');
          return await downloadWithToken(newToken);
        }
        throw new Error('Сессия истекла, требуется повторная авторизация');
      }
      
      if (!response.ok) {
        let errorMsg = 'Ошибка скачивания PDF';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          // Если ответ не JSON, используем текст статуса
          errorMsg = `${errorMsg} (${response.status})`;
        }
        throw new Error(errorMsg);
      }
      
      return response;
    };
    
    try {
      const response = await downloadWithToken();
      
      // Получаем blob
      const blob = await response.blob();
      
      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Извлекаем имя файла из заголовка Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `DeepLog_Report_${taskId}.pdf`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Очистка
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Ошибка скачивания PDF:', error);
      throw error;
    }
  },

  // Получение данных аналитики для дашборда
  getAnalytics: async (taskId) => {
    return await apiRequest(`log-analysis/tasks/${taskId}/analytics`, {
      method: 'GET'
    });
  }
};

// OCR Scanner API
export const ocrScannerAPI = {
  // Загрузка документа для распознавания
  uploadDocument: async (formData) => {
    return await uploadFileRequest('ocr-scanner/upload', formData);
  },

  uploadDocumentBundle: async (formData) => {
    return await uploadFileRequest('ocr-scanner/upload-bundle', formData);
  },

  // Получение списка задач
  getTasks: async (params = {}, options = {}) => {
    const searchParams = new URLSearchParams();

    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.status) searchParams.append('status_filter', params.status);

    const queryString = searchParams.toString();
    return await apiRequest(`ocr-scanner/tasks${queryString ? '?' + queryString : ''}`, options);
  },

  // Получение конкретной задачи
  getTask: async (taskId) => {
    return await apiRequest(`ocr-scanner/tasks/${taskId}`);
  },

  // Получение статуса задачи
  getTaskStatus: async (taskId) => {
    return await apiRequest(`ocr-scanner/tasks/${taskId}/status`);
  },

  // Удаление задачи
  deleteTask: async (taskId) => {
    return await apiRequest(`ocr-scanner/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  // ===== ШАБЛОНЫ =====
  
  // Получение списка шаблонов
  getTemplates: async (documentType = null, options = {}) => {
    const params = new URLSearchParams();
    if (documentType) params.append('document_type', documentType);
    const query = params.toString();
    return await apiRequest(`ocr-scanner/templates${query ? '?' + query : ''}`, options);
  },

  // Получение конкретного шаблона
  getTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}`);
  },

  exportTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}/export`);
  },

  importTemplate: async (file) => {
    const formData = new FormData();
    formData.append('import_file', file);
    return await uploadFileRequest('ocr-scanner/templates/import', formData);
  },

  // Создание шаблона
  createTemplate: async (templateData) => {
    const formData = new FormData();
    Object.keys(templateData).forEach(key => {
      if (templateData[key] !== undefined && templateData[key] !== null) {
        if (typeof templateData[key] === 'object') {
          formData.append(key, JSON.stringify(templateData[key]));
        } else {
          formData.append(key, templateData[key]);
        }
      }
    });
    return await uploadFileRequest('ocr-scanner/templates', formData);
  },

  // Обновление метаданных шаблона
  updateTemplate: async (templateId, templateData) => {
    const formData = new FormData();
    Object.keys(templateData).forEach(key => {
      if (templateData[key] !== undefined && templateData[key] !== null) {
        if (typeof templateData[key] === 'object') {
          formData.append(key, JSON.stringify(templateData[key]));
        } else {
          formData.append(key, templateData[key]);
        }
      }
    });
    return await apiRequest(`ocr-scanner/templates/${templateId}`, {
      method: 'PUT',
      body: formData
    });
  },

  getTemplateFlow: async (templateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}/flow`);
  },

  updateTemplateFlow: async (templateId, flowConfig) => {
    const formData = new FormData();
    formData.append('flow_config', JSON.stringify(flowConfig || {}));
    return await apiRequest(`ocr-scanner/templates/${templateId}/flow`, {
      method: 'PUT',
      body: formData
    });
  },

  getExportTemplates: async (templateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}/export-templates`);
  },

  createExportTemplate: async (templateId, data) => {
    const formData = new FormData();
    Object.keys(data || {}).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        if (typeof data[key] === 'object') {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/export-templates`, formData);
  },

  updateExportTemplate: async (exportTemplateId, data) => {
    const formData = new FormData();
    Object.keys(data || {}).forEach(key => {
      if (data[key] !== undefined && data[key] !== null) {
        if (typeof data[key] === 'object') {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    });
    return await apiRequest(`ocr-scanner/export-templates/${exportTemplateId}`, {
      method: 'PUT',
      body: formData
    });
  },

  deleteExportTemplate: async (exportTemplateId) => {
    return await apiRequest(`ocr-scanner/export-templates/${exportTemplateId}`, {
      method: 'DELETE'
    });
  },

  // Удаление шаблона
  deleteTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}`, {
      method: 'DELETE'
    });
  },

  // Дублирование шаблона
  duplicateTemplate: async (templateId, newName = null) => {
    const formData = new FormData();
    if (newName) formData.append('new_name', newName);
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/duplicate`, formData);
  },

  // Добавление поля к шаблону
  addTemplateField: async (templateId, fieldData) => {
    const formData = new FormData();
    Object.keys(fieldData).forEach(key => {
      if (fieldData[key] !== undefined && fieldData[key] !== null) {
        if (typeof fieldData[key] === 'object') {
          formData.append(key, JSON.stringify(fieldData[key]));
        } else {
          formData.append(key, fieldData[key]);
        }
      }
    });
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/fields`, formData);
  },

  // Удаление поля шаблона
  deleteTemplateField: async (templateId, fieldId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}/fields/${fieldId}`, {
      method: 'DELETE'
    });
  },

  // Переключение замка поля
  toggleFieldLock: async (templateId, fieldId) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ocr-scanner/templates/${templateId}/fields/${fieldId}/toggle-lock`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Ошибка при переключении замка');
    }
    
    return response.json();
  },

  // Обновление поля шаблона
  updateTemplateField: async (templateId, fieldId, fieldData) => {
    const formData = new FormData();
    Object.keys(fieldData).forEach(key => {
      if (key === 'constant_value') return;
      if (fieldData[key] !== undefined && fieldData[key] !== null) {
        if (typeof fieldData[key] === 'object') {
          formData.append(key, JSON.stringify(fieldData[key]));
        } else {
          formData.append(key, fieldData[key]);
        }
      }
    });
    if (Object.prototype.hasOwnProperty.call(fieldData, 'constant_value')) {
      formData.append('constant_value', fieldData.constant_value ?? '');
      formData.append('constant_value_provided', 'true');
    }
    
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ocr-scanner/templates/${templateId}/fields/${fieldId}`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Ошибка запроса' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    
    return await response.json();
  },

  // Изменение порядка полей
  reorderTemplateFields: async (templateId, fieldIds) => {
    const formData = new FormData();
    formData.append('field_ids', JSON.stringify(fieldIds));
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/fields/reorder`, formData);
  },

  // Применение шаблона к задаче
  applyTemplateToTask: async (templateId, taskId) => {
    const formData = new FormData();
    formData.append('task_id', taskId);
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/apply`, formData);
  },

  importExcelToTemplate: async (templateId, file, selectedSheets = []) => {
    const formData = new FormData();
    formData.append('import_file', file);
    if (selectedSheets && selectedSheets.length > 0) {
      formData.append('selected_sheets', JSON.stringify(selectedSheets));
    }
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/import-excel`, formData);
  },

  inspectExcelTemplate: async (file) => {
    const formData = new FormData();
    formData.append('import_file', file);
    return await uploadFileRequest('ocr-scanner/templates/inspect-excel', formData);
  },

  getReusableTemplateGroups: async (templateId, sourceTemplateId) => {
    return await apiRequest(`ocr-scanner/templates/${templateId}/reusable-groups?source_template_id=${encodeURIComponent(sourceTemplateId)}`);
  },

  importTemplateGroups: async (templateId, sourceTemplateId, groupNames = []) => {
    const formData = new FormData();
    formData.append('source_template_id', sourceTemplateId);
    formData.append('group_names', JSON.stringify(groupNames));
    return await uploadFileRequest(`ocr-scanner/templates/${templateId}/import-groups`, formData);
  },

  // ===== ШАБЛОНЫ ЗОН =====
  getZoneTemplates: async (options = {}) => {
    return await apiRequest('ocr-scanner/zone-templates', options);
  },

  getZoneTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/zone-templates/${templateId}`);
  },

  exportZoneTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/zone-templates/${templateId}/export`);
  },

  importZoneTemplate: async (file) => {
    const formData = new FormData();
    formData.append('import_file', file);
    return await uploadFileRequest('ocr-scanner/zone-templates/import', formData);
  },

  createZoneTemplate: async (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) formData.append('description', data.description);
    formData.append('alignment_mode', data.alignment_mode || 'auto_align');
    formData.append('page_scope', data.page_scope || 'all_pages_same');
    formData.append('reference_page', String(data.reference_page || 1));
    formData.append('reference_file', data.reference_file);
    return await uploadFileRequest('ocr-scanner/zone-templates', formData);
  },

  saveZoneTemplateAreas: async (templateId, areas) => {
    const formData = new FormData();
    formData.append('areas', JSON.stringify(areas || []));

    const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    const response = await fetch(`/api/ocr-scanner/zone-templates/${templateId}/areas`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Ошибка запроса' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    return await response.json();
  },

  deleteZoneTemplate: async (templateId) => {
    return await apiRequest(`ocr-scanner/zone-templates/${templateId}`, {
      method: 'DELETE'
    });
  },

  getZoneTemplatePreview: async (templateId, page = null) => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
    const query = page ? `?page=${encodeURIComponent(page)}` : '';
    const response = await fetch(`/api/ocr-scanner/zone-templates/${templateId}/preview${query}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    if (!response.ok) {
      throw new Error(`Preview HTTP ${response.status}`);
    }
    return await response.blob();
  },

  // Экспорт результата задачи
  exportTaskResult: async (taskId, format = 'excel', exportTemplateId = null) => {
    const params = new URLSearchParams({ format });
    if (exportTemplateId) params.append('export_template_id', exportTemplateId);

    const downloadExport = async (token = localStorage.getItem('accessToken') || localStorage.getItem('access_token')) => (
      await fetch(`/api/ocr-scanner/tasks/${taskId}/export?${params.toString()}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })
    );

    let response = await downloadExport();
    if (response.status === 401) {
      const newToken = await refreshAuthToken();
      if (newToken) {
        response = await downloadExport(newToken);
      }
    }
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    return { data: blob };
  },

  // Получение структурированных результатов
  getStructuredResult: async (taskId) => {
    return await apiRequest(`ocr-scanner/tasks/${taskId}/structured`);
  },

  // Повторное извлечение данных с выбором метода
  reExtractData: async (taskId, method = 'llm') => {
    const formData = new FormData();
    formData.append('method', method);
    return await uploadFileRequest(`ocr-scanner/tasks/${taskId}/re-extract`, formData);
  },

  // ===== СПРАВОЧНИКИ =====
  
  // Получение списка справочников
  getDictionaries: async (scope = null, templateId = null, options = {}) => {
    const params = new URLSearchParams();
    if (scope) params.append('scope', scope);
    if (templateId) params.append('template_id', templateId);
    const query = params.toString();
    return await apiRequest(`ocr-scanner/dictionaries${query ? '?' + query : ''}`, options);
  },

  // Создание справочника
  createDictionary: async (dictionaryData) => {
    const formData = new FormData();
    Object.keys(dictionaryData).forEach(key => {
      if (dictionaryData[key] !== undefined && dictionaryData[key] !== null) {
        formData.append(key, dictionaryData[key]);
      }
    });
    return await uploadFileRequest('ocr-scanner/dictionaries', formData);
  },

  // Обновление справочника
  updateDictionary: async (dictionaryId, dictionaryData) => {
    const formData = new FormData();
    Object.keys(dictionaryData).forEach(key => {
      if (dictionaryData[key] !== undefined && dictionaryData[key] !== null) {
        formData.append(key, dictionaryData[key]);
      }
    });

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ocr-scanner/dictionaries/${dictionaryId}`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Ошибка запроса' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  },

  // Удаление справочника
  deleteDictionary: async (dictionaryId) => {
    return await apiRequest(`ocr-scanner/dictionaries/${dictionaryId}`, {
      method: 'DELETE'
    });
  },

  // Добавление правила в справочник
  addDictionaryRule: async (dictionaryId, ruleData) => {
    const formData = new FormData();
    Object.keys(ruleData).forEach(key => {
      if (ruleData[key] !== undefined && ruleData[key] !== null) {
        if (typeof ruleData[key] === 'object') {
          formData.append(key, JSON.stringify(ruleData[key]));
        } else {
          formData.append(key, ruleData[key]);
        }
      }
    });
    return await uploadFileRequest(`ocr-scanner/dictionaries/${dictionaryId}/rules`, formData);
  },

  // Получение правил справочника
  getDictionaryRules: async (dictionaryId) => {
    return await apiRequest(`ocr-scanner/dictionaries/${dictionaryId}/rules`);
  },

  // Полная замена правил справочника
  replaceDictionaryRules: async (dictionaryId, rulesJson) => {
    const formData = new FormData();
    formData.append('rules_json', JSON.stringify(rulesJson));

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ocr-scanner/dictionaries/${dictionaryId}/rules`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Ошибка запроса' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  },

  // Импорт правил в справочник
  importDictionaryRules: async (dictionaryId, rulesJson) => {
    const formData = new FormData();
    formData.append('rules_json', JSON.stringify(rulesJson));
    return await uploadFileRequest(`ocr-scanner/dictionaries/${dictionaryId}/import`, formData);
  },

  previewReplacementDictionaryExcel: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await uploadFileRequest('ocr-scanner/dictionaries/preview-excel', formData);
  }
};

// Info Banner API
export const infoBannerAPI = {
  // Получение активного баннера
  getActiveBanner: async () => {
    return await apiRequest('/info-banner/active');
  },

  // Получение всех баннеров (только для админов)
  getAllBanners: async () => {
    return await apiRequest('/info-banner/all');
  },

  // Создание нового баннера (только для админов)
  createBanner: async (bannerData) => {
    return await apiRequest('/info-banner/', {
      method: 'POST',
      body: JSON.stringify(bannerData)
    });
  },

  // Обновление баннера (только для админов)
  updateBanner: async (bannerId, bannerData) => {
    return await apiRequest(`/info-banner/${bannerId}`, {
      method: 'PATCH',
      body: JSON.stringify(bannerData)
    });
  },

  // Удаление баннера (только для админов)
  deleteBanner: async (bannerId) => {
    return await apiRequest(`/info-banner/${bannerId}`, {
      method: 'DELETE'
    });
  },

  // Деактивация баннера (только для админов)
  deactivateBanner: async (bannerId) => {
    return await apiRequest(`/info-banner/${bannerId}/deactivate`, {
      method: 'POST'
    });
  }
};

// AI Chat API - чат с LLM моделями
export const aiChatAPI = {
  getToolSettings: async () => {
    return await apiRequest('/ai-chat/tool-settings');
  },

  getImageModels: async () => {
    return await apiRequest('/ai-chat/image-models');
  },

  // Проекты чатов
  getProjects: async () => {
    return await apiRequest('/ai-chat/projects');
  },

  createProject: async (data) => {
    return await apiRequest('/ai-chat/projects', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateProject: async (projectId, data) => {
    return await apiRequest(`/ai-chat/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteProject: async (projectId) => {
    return await apiRequest(`/ai-chat/projects/${projectId}`, {
      method: 'DELETE'
    });
  },

  // Получение доступных моделей
  getModels: async () => {
    return await apiRequest('/ai-chat/models');
  },

  // Получение списка чатов
  getConversations: async (page = 1, limit = 20, status = null) => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status_filter', status);
    return await apiRequest(`/ai-chat/conversations?${params}`);
  },

  // Создание нового чата
  createConversation: async (data = {}) => {
    return await apiRequest('/ai-chat/conversations', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Получение чата с сообщениями
  getConversation: async (conversationId) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}`);
  },

  // Обновление чата
  updateConversation: async (conversationId, data) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Удаление чата
  deleteConversation: async (conversationId) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}`, {
      method: 'DELETE'
    });
  },

  stopGeneration: async (conversationId) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}/generation/stop`, {
      method: 'POST',
    });
  },

  // Отправка сообщения
  sendMessage: async (conversationId, content, options = {}) => {
    return await longRunningApiRequest(`/ai-chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, ...options })
    });
  },

  // Отправка сообщения со стримингом
  sendMessageStream: async (conversationId, content, options = {}, onChunk, signal) => {
    const token = localStorage.getItem('accessToken');
    let reader = null;
    
    try {
      const response = await fetch(`/api/ai-chat/conversations/${conversationId}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content, ...options }),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = typeof errorData?.detail === 'string'
          ? errorData.detail
          : (errorData?.error?.message || errorData?.message || `HTTP ${response.status}`);
        throw new Error(detail);
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (signal?.aborted) {
          throw signal.reason instanceof Error
            ? signal.reason
            : new DOMException('Aborted', 'AbortError');
        }
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (onChunk) onChunk(data);
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      if (reader) {
        try {
          await reader.cancel();
        } catch (cancelError) {
          // ignore
        }
      }
      if (isAbortError(error) || signal?.aborted) {
        throw (isAbortError(error) ? error : new DOMException('Aborted', 'AbortError'));
      }
      console.error('Stream error:', error);
      throw error;
    }
  },

  uploadAttachment: async (conversationId, file) => {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`/api/ai-chat/conversations/${conversationId}/attachments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.detail || `HTTP ${response.status}`);
    }
    return await response.json();
  },

  getAttachments: async (conversationId) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}/attachments`);
  },

  deleteAttachment: async (conversationId, attachmentId) => {
    return await apiRequest(`/ai-chat/conversations/${conversationId}/attachments/${attachmentId}`, {
      method: 'DELETE'
    });
  },

  // MCP servers
  getMCPServers: async () => {
    return await apiRequest('/ai-chat/mcp/servers');
  },

  createMCPServer: async (data) => {
    return await apiRequest('/ai-chat/mcp/servers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateMCPServer: async (serverId, data) => {
    return await apiRequest(`/ai-chat/mcp/servers/${serverId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteMCPServer: async (serverId) => {
    return await apiRequest(`/ai-chat/mcp/servers/${serverId}`, {
      method: 'DELETE'
    });
  },

  testMCPServer: async (data) => {
    return await apiRequest('/ai-chat/mcp/servers/test', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getMCPServerTools: async (serverId) => {
    return await apiRequest(`/ai-chat/mcp/servers/${serverId}/tools`);
  },

  getSkills: async () => {
    return await apiRequest('/ai-chat/skills');
  },

  createSkill: async (data) => {
    return await apiRequest('/ai-chat/skills', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateSkill: async (skillId, data) => {
    return await apiRequest(`/ai-chat/skills/${skillId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteSkill: async (skillId) => {
    return await apiRequest(`/ai-chat/skills/${skillId}`, {
      method: 'DELETE'
    });
  },

  // Быстрый чат без сохранения
  quickChat: async (content, modelId = null, options = {}) => {
    const params = modelId ? `?model_id=${modelId}` : '';
    return await longRunningApiRequest(`/ai-chat/quick-chat${params}`, {
      method: 'POST',
      body: JSON.stringify({ content, ...options })
    });
  },

  // Экспорт чата в выбранном формате
  exportConversation: async (conversationId, format = 'pdf') => {
    const token = localStorage.getItem('accessToken');
    const normalizedFormat = (format || 'pdf').toLowerCase();
    const allowedFormats = ['pdf', 'docx'];
    if (!allowedFormats.includes(normalizedFormat)) {
      throw new Error(`Неподдерживаемый формат экспорта: ${format}`);
    }
    
    try {
      const response = await fetch(`/api/ai-chat/conversations/${conversationId}/export/${normalizedFormat}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Ошибка экспорта: ${response.statusText}`);
      }
      
      // Получаем имя файла из заголовка Content-Disposition
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `chat_export.${normalizedFormat}`;
      if (contentDisposition) {
        const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8FilenameMatch?.[1]) {
          filename = decodeURIComponent(utf8FilenameMatch[1]);
        } else {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1];
          }
        }
      }
      
      // Создаем blob и скачиваем файл
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Ошибка экспорта чата:', error);
      throw error;
    }
  },

  exportToPDF: async (conversationId) => {
    return await aiChatAPI.exportConversation(conversationId, 'pdf');
  },

  exportToDOCX: async (conversationId) => {
    return await aiChatAPI.exportConversation(conversationId, 'docx');
  }
};


export const ragV2DocumentsAPI = {
  uploadDocument: async (formData) => {
    return await uploadFileRequest('rag-v2/documents/upload', formData, 2);
  },
};

// Image Generation API - генерация изображений через Z-Image-Turbo
export const imageGenerationAPI = {
  // Получить статус сервиса генерации изображений
  getStatus: async () => {
    return await apiRequest('/image-generation/status');
  },

  // Сгенерировать изображение
  generate: async (data) => {
    return await longRunningApiRequest('/image-generation/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Предзагрузка модели (только для админов)
  preloadModel: async () => {
    return await apiRequest('/image-generation/preload', {
      method: 'POST'
    });
  }
};

export const getPublicTokenPricing = async () => {
  const response = await fetch('/api/ai-chat/public/token-pricing', {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error('Не удалось загрузить коэффициенты');
  }
  return response.json();
};

// AI Chat Admin API - управление провайдерами и моделями
export const aiChatAdminAPI = {
  // Прокси
  getProxies: async () => {
    return await apiRequest('/admin/ai-chat/proxies');
  },

  createProxy: async (data) => {
    return await apiRequest('/admin/ai-chat/proxies', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateProxy: async (proxyId, data) => {
    return await apiRequest(`/admin/ai-chat/proxies/${proxyId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteProxy: async (proxyId) => {
    return await apiRequest(`/admin/ai-chat/proxies/${proxyId}`, {
      method: 'DELETE'
    });
  },

  checkProxyConnection: async (proxyId) => {
    return await apiRequest(`/admin/ai-chat/proxies/${proxyId}/check-connection`, {
      method: 'POST'
    });
  },

  getToolSettings: async () => {
    return await apiRequest('/admin/ai-chat/tool-settings');
  },

  updateToolSettings: async (tools) => {
    return await apiRequest('/admin/ai-chat/tool-settings', {
      method: 'PUT',
      body: JSON.stringify({ tools })
    });
  },

  getImageModelSettings: async () => {
    return await apiRequest('/admin/ai-chat/image-model-settings');
  },

  getTokenPricingSettings: async () => {
    return await apiRequest('/admin/ai-chat/token-pricing');
  },

  updateTokenPricingSettings: async (enabled) => {
    return await apiRequest('/admin/ai-chat/token-pricing', {
      method: 'PUT',
      body: JSON.stringify({ enabled })
    });
  },

  updateImageModelSettings: async (models) => {
    return await apiRequest('/admin/ai-chat/image-model-settings', {
      method: 'PUT',
      body: JSON.stringify({ models })
    });
  },

  getDataProtectionSettings: async () => {
    return await apiRequest('/admin/ai-chat/data-protection/settings');
  },

  updateDataProtectionSettings: async (data) => {
    return await apiRequest('/admin/ai-chat/data-protection/settings', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  getDataProtectionPolicies: async () => {
    return await apiRequest('/admin/ai-chat/data-protection/policies');
  },

  createDataProtectionPolicy: async (data) => {
    return await apiRequest('/admin/ai-chat/data-protection/policies', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateDataProtectionPolicy: async (policyId, data) => {
    return await apiRequest(`/admin/ai-chat/data-protection/policies/${policyId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteDataProtectionPolicy: async (policyId) => {
    return await apiRequest(`/admin/ai-chat/data-protection/policies/${policyId}`, {
      method: 'DELETE'
    });
  },

  addDataProtectionRule: async (policyId, data) => {
    return await apiRequest(`/admin/ai-chat/data-protection/policies/${policyId}/rules`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateDataProtectionRule: async (policyId, ruleId, data) => {
    return await apiRequest(`/admin/ai-chat/data-protection/policies/${policyId}/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteDataProtectionRule: async (policyId, ruleId) => {
    return await apiRequest(`/admin/ai-chat/data-protection/policies/${policyId}/rules/${ruleId}`, {
      method: 'DELETE'
    });
  },

  getModelPolicyBindings: async (modelId) => {
    return await apiRequest(`/admin/ai-chat/data-protection/models/${modelId}/policies`);
  },

  updateModelPolicyBindings: async (modelId, bindings) => {
    return await apiRequest(`/admin/ai-chat/data-protection/models/${modelId}/policies`, {
      method: 'PUT',
      body: JSON.stringify({ bindings })
    });
  },

  getDataProtectionAudit: async (limit = 50, offset = 0) => {
    return await apiRequest(`/admin/ai-chat/data-protection/audit?limit=${limit}&offset=${offset}`);
  },

  // Провайдеры
  getProviders: async () => {
    return await apiRequest('/admin/ai-chat/providers');
  },

  createProvider: async (data) => {
    return await apiRequest('/admin/ai-chat/providers', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateProvider: async (providerId, data) => {
    return await apiRequest(`/admin/ai-chat/providers/${providerId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteProvider: async (providerId) => {
    return await apiRequest(`/admin/ai-chat/providers/${providerId}`, {
      method: 'DELETE'
    });
  },

  checkProviderHealth: async (providerId) => {
    return await apiRequest(`/admin/ai-chat/providers/${providerId}/check-health`, {
      method: 'POST'
    });
  },

  getAvailableModels: async (providerId) => {
    return await apiRequest(`/admin/ai-chat/providers/${providerId}/available-models`);
  },

  // Модели
  getModels: async (providerId = null) => {
    const params = providerId ? `?provider_id=${providerId}` : '';
    return await apiRequest(`/admin/ai-chat/models${params}`);
  },

  createModel: async (data) => {
    return await apiRequest('/admin/ai-chat/models', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateModel: async (modelId, data) => {
    return await apiRequest(`/admin/ai-chat/models/${modelId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteModel: async (modelId) => {
    return await apiRequest(`/admin/ai-chat/models/${modelId}`, {
      method: 'DELETE'
    });
  },

  // Статистика
  getStats: async (days = 30) => {
    return await apiRequest(`/admin/ai-chat/stats?days=${days}`);
  },

  getLlmGatewayStats: async () => {
    return await apiRequest('/admin/ai-chat/llm-gateway/stats');
  },

  resetLlmGatewayPool: async (poolId) => {
    return await apiRequest(`/admin/ai-chat/llm-gateway/pools/${poolId}/reset`, {
      method: 'POST'
    });
  },

  // Создание дефолтных провайдеров
  seedDefaults: async () => {
    return await apiRequest('/admin/ai-chat/seed-defaults', {
      method: 'POST'
    });
  }
};

export const llmFilterAPI = {
  // Хелпер для построения базового URL по scope
  _baseUrl: (scope = 'admin', companyId = null) => {
    if (scope === 'company' && companyId) {
      return `/company/${companyId}/llm-filter`;
    }
    return '/admin/llm-filter';
  },

  // Обзор (дашборд)
  getOverview: async (scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/overview`);
  },

  // Каталог правил guardrails
  getRulesCatalog: async (params = {}, scope = 'admin', companyId = null) => {
    const query = new URLSearchParams();
    if (params.data_type) query.set('data_type', params.data_type);
    if (params.q) query.set('q', params.q);
    const qs = query.toString();
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/rules/catalog${qs ? `?${qs}` : ''}`);
  },

  getRulesCatalogStats: async (scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/rules/catalog/stats`);
  },

  createCatalogRule: async (data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/rules/catalog`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateCatalogRule: async (ruleId, data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/rules/catalog/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteCatalogRule: async (ruleId, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/rules/catalog/${ruleId}`, {
      method: 'DELETE'
    });
  },

  // Глобальные настройки
  getSettings: async (scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/settings`);
  },

  updateSettings: async (data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Политики
  getPolicies: async (scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies`);
  },

  createPolicy: async (data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updatePolicy: async (policyId, data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies/${policyId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deletePolicy: async (policyId, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies/${policyId}`, {
      method: 'DELETE'
    });
  },

  // Правила политики
  addRule: async (policyId, data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies/${policyId}/rules`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateRule: async (policyId, ruleId, data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies/${policyId}/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteRule: async (policyId, ruleId, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/policies/${policyId}/rules/${ruleId}`, {
      method: 'DELETE'
    });
  },

  // Привязки политик к моделям
  getModelBindings: async (modelId, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/models/${modelId}/policies`);
  },

  updateModelBindings: async (modelId, bindings, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/models/${modelId}/policies`, {
      method: 'PUT',
      body: JSON.stringify({ bindings })
    });
  },

  // Песочница
  runSandbox: async (data, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/sandbox/run`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Аудит
  getAudit: async (params = {}, scope = 'admin', companyId = null) => {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    if (params.action) query.set('action', params.action);
    if (params.provider) query.set('provider', params.provider);
    if (params.application) query.set('application', params.application);
    if (params.user) query.set('user', params.user);
    if (params.source) query.set('source', params.source);
    if (params.company) query.set('company', params.company);
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/audit?${query.toString()}`);
  },

  getAuditDetail: async (auditId, scope = 'admin', companyId = null) => {
    return await apiRequest(`${llmFilterAPI._baseUrl(scope, companyId)}/audit/${auditId}`);
  },

  // Список компаний для фильтра аудита (только admin)
  getAuditCompanies: async () => {
    return await apiRequest('/admin/llm-filter/audit/companies');
  },

  // Модели (для песочницы) — admin использует ai-chat, company — свой эндпоинт
  getModels: async (scope = 'admin', companyId = null) => {
    if (scope === 'company' && companyId) {
      return await apiRequest(`/company/${companyId}/llm-filter/models`);
    }
    return await apiRequest('/admin/ai-chat/models');
  }
};

const PROTOCOL_RICH_HTML_PATTERN = /<(table|thead|tbody|tr|td|th|p|ul|ol|li|h[1-6]|blockquote)\b/i;

const escapeProtocolHtml = (value = '') => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const stripSimpleProtocolWrappers = (value = '') => String(value || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/?(div|span)[^>]*>/gi, '')
  .trim();

const normalizeProtocolTableCells = (line = '') => String(line || '')
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map((cell) => cell.trim());

const isProtocolTableSeparator = (line = '') => /^[:\-\|\s]+$/.test(String(line || '').trim());

const isProtocolHeading = (line = '') => {
  const normalized = String(line || '')
    .replace(/\*\*/g, '')
    .replace(/#+\s*/, '')
    .trim();

  if (!normalized) return false;

  return [
    'краткое содержание',
    'что обсудили',
    'о чем договорились',
    'задачи',
    'оставшиеся вопросы',
    'особые наблюдения',
    'решения',
    'рекомендации ai',
  ].some((heading) => normalized.toLowerCase().replace(/:$/, '') === heading);
};

export const composeProtocolHtml = (protocolText, extra = {}) => {
  if (!protocolText || !String(protocolText).trim()) {
    return '';
  }

  const sourceText = String(protocolText || '').trim();
  if (PROTOCOL_RICH_HTML_PATTERN.test(sourceText)) {
    return sourceText;
  }

  const cleanedText = stripSimpleProtocolWrappers(sourceText);
  const lines = cleanedText.replace(/\r\n/g, '\n').split('\n');
  const htmlParts = [];
  let index = 0;

  while (index < lines.length) {
    const rawLine = lines[index];
    const line = String(rawLine || '').trim();

    if (!line) {
      index += 1;
      continue;
    }

    const nextLine = String(lines[index + 1] || '').trim();
    const isTableHeader = line.includes('|') && isProtocolTableSeparator(nextLine);
    if (isTableHeader) {
      const tableLines = [line];
      index += 2;
      while (index < lines.length) {
        const candidate = String(lines[index] || '').trim();
        if (!candidate || !candidate.includes('|')) break;
        tableLines.push(candidate);
        index += 1;
      }

      const headers = normalizeProtocolTableCells(tableLines[0]);
      const rows = tableLines.slice(1)
        .map(normalizeProtocolTableCells)
        .filter((cells) => cells.some((cell) => cell));

      if (headers.length > 0 && rows.length > 0) {
        const headHtml = headers.map((cell) => `<th class="border border-gray-300 px-3 py-2 text-left font-semibold">${escapeProtocolHtml(cell)}</th>`).join('');
        const bodyHtml = rows.map((row) => {
          const normalizedRow = [...row];
          while (normalizedRow.length < headers.length) normalizedRow.push('');
          return `<tr>${normalizedRow.slice(0, headers.length).map((cell) => `<td class="border border-gray-300 px-3 py-2 align-top">${escapeProtocolHtml(cell)}</td>`).join('')}</tr>`;
        }).join('');
        htmlParts.push(`<div class="my-3 overflow-x-auto"><table class="min-w-full border-collapse text-sm"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`);
        continue;
      }
    }

    if (/^#{1,6}\s+/.test(line) || isProtocolHeading(line)) {
      const headingText = line
        .replace(/^#{1,6}\s+/, '')
        .replace(/\*\*/g, '')
        .replace(/:$/, '')
        .trim();
      htmlParts.push(`<h3 class="mt-4 mb-2 text-base font-semibold text-gray-900">${escapeProtocolHtml(headingText)}</h3>`);
      index += 1;
      continue;
    }

    if (/^[-*•]\s+/.test(line)) {
      const listItems = [];
      while (index < lines.length) {
        const itemLine = String(lines[index] || '').trim();
        if (!/^[-*•]\s+/.test(itemLine)) break;
        listItems.push(itemLine.replace(/^[-*•]\s+/, '').trim());
        index += 1;
      }
      htmlParts.push(`<ul class="my-2 list-disc space-y-1 pl-5">${listItems.map((item) => `<li>${escapeProtocolHtml(item)}</li>`).join('')}</ul>`);
      continue;
    }

    const paragraphLines = [line];
    index += 1;
    while (index < lines.length) {
      const candidate = String(lines[index] || '').trim();
      const candidateNext = String(lines[index + 1] || '').trim();
      if (!candidate) break;
      if (/^[-*•]\s+/.test(candidate) || /^#{1,6}\s+/.test(candidate) || isProtocolHeading(candidate)) break;
      if (candidate.includes('|') && isProtocolTableSeparator(candidateNext)) break;
      paragraphLines.push(candidate);
      index += 1;
    }

    htmlParts.push(`<p class="mb-2 whitespace-pre-wrap break-words">${escapeProtocolHtml(paragraphLines.join(' '))}</p>`);
  }

  if (htmlParts.length === 0) {
    return `<div class="formatted-ai-content text-sm text-gray-800"><p class="mb-0 whitespace-pre-wrap break-words">${escapeProtocolHtml(cleanedText)}</p></div>`;
  }

  return `<div class="formatted-ai-content text-sm text-gray-800">${htmlParts.join('')}</div>`;
};

// API для функционала "Моя компания"
export const companyAPI = {
  // Создание компании
  createCompany: async (companyData) => {
    return await apiRequest('/company/', {
      method: 'POST',
      body: JSON.stringify(companyData)
    });
  },

  // Получение информации о своей компании
  getMyCompany: async () => {
    return await apiRequest('/company/my');
  },

  // Получение детальной информации о компании
  getCompany: async (companyId) => {
    return await apiRequest(`/company/${companyId}`);
  },

  getCompanySso: async (companyId) => {
    return await apiRequest(`/company/${companyId}/sso`);
  },

  updateCompanySso: async (companyId, data) => {
    return await apiRequest(`/company/${companyId}/sso`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  testCompanySso: async (companyId, provider) => {
    return await apiRequest(`/company/${companyId}/sso/test?provider=${encodeURIComponent(provider)}`, {
      method: 'POST',
    });
  },

  // Обновление компании
  updateCompany: async (companyId, companyData) => {
    return await apiRequest(`/company/${companyId}`, {
      method: 'PUT',
      body: JSON.stringify(companyData)
    });
  },

  // Удаление компании
  deleteCompany: async (companyId) => {
    return await apiRequest(`/company/${companyId}`, {
      method: 'DELETE'
    });
  },

  // Удаление участника из компании
  removeMember: async (companyId, userId) => {
    return await apiRequest(`/company/${companyId}/members/remove/${userId}`, {
      method: 'POST'
    });
  },

  // Изменение роли участника (назначение/снятие администратора)
  updateMemberRole: async (companyId, userId, role) => {
    return await apiRequest(`/company/${companyId}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role })
    });
  },

  // Выход из компании
  leaveCompany: async (companyId) => {
    return await apiRequest(`/company/leave/${companyId}`, {
      method: 'POST'
    });
  },

  // Создание приглашения
  createInvitation: async (companyId, email) => {
    return await apiRequest(`/company/${companyId}/invitations`, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  // Массовое создание приглашений
  createBulkInvitations: async (companyId, emails) => {
    return await apiRequest(`/company/${companyId}/invitations/bulk`, {
      method: 'POST',
      body: JSON.stringify({ emails })
    });
  },

  // Получение списка приглашений
  getInvitations: async (companyId) => {
    return await apiRequest(`/company/${companyId}/invitations`);
  },

  // Отмена приглашения
  cancelInvitation: async (companyId, invitationId) => {
    return await apiRequest(`/company/${companyId}/invitations/${invitationId}`, {
      method: 'DELETE'
    });
  },

  // Присоединение по токену приглашения
  joinByToken: async (token) => {
    return await apiRequest(`/company/join/token?token=${encodeURIComponent(token)}`, {
      method: 'POST'
    });
  },

  // Присоединение по коду компании
  joinByCode: async (companyCode) => {
    return await apiRequest('/company/join/code', {
      method: 'POST',
      body: JSON.stringify({ company_code: companyCode })
    });
  },

  // Присоединение по постоянной ссылке (когда код приглашения скрыт)
  joinByLink: async (token) => {
    return await apiRequest('/company/join/link', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  },

  // Активация компании
  activateCompany: async (companyId) => {
    return await apiRequest(`/company/activate/${companyId}`, {
      method: 'POST'
    });
  },

  // Деактивация компании (переход в личный режим)
  deactivateCompany: async () => {
    return await apiRequest('/company/deactivate', {
      method: 'POST'
    });
  },

  // Получение статистики компании
  getCompanyStats: async (companyId) => {
    return await apiRequest(`/company/${companyId}/stats`);
  },

  // Расширенный дашборд статистики компании
  getCompanyDashboard: async (companyId, params = {}) => {
    const query = new URLSearchParams();
    if (params.period) query.append('period', params.period);
    if (params.date_from) query.append('date_from', params.date_from);
    if (params.date_to) query.append('date_to', params.date_to);
    return await apiRequest(`/company/${companyId}/dashboard?${query.toString()}`);
  },

  getCompanyAnalytics: async (companyId, { period = 'month', dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && dateFrom) params.append('date_from', dateFrom);
    if (period === 'custom' && dateTo) params.append('date_to', dateTo);
    return await apiRequest(`/company/${companyId}/analytics?${params}`);
  },

  exportCompanyAnalyticsToXlsx: async (companyId, { period = 'month', dateFrom = '', dateTo = '' } = {}) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && dateFrom) params.append('date_from', dateFrom);
    if (period === 'custom' && dateTo) params.append('date_to', dateTo);

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/company/${companyId}/analytics/export?${params}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Ошибка HTTP: ${response.status}. ${errorText}`);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'company_analytics.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }, 100);

    return { success: true, filename, size: blob.size };
  },

  // Проверка приглашения (публичный)
  checkInvitation: async (token) => {
    return await apiRequest(`/company/invitation/check/${token}`);
  },

  // Проверка постоянной ссылки-приглашения (публичный)
  checkInviteLink: async (token) => {
    return await apiRequest(`/company/invite-link/check/${encodeURIComponent(token)}`);
  },

  // =====================================================
  // Квоты токенов публичных моделей
  // =====================================================
  getPublicModelQuotas: async (companyId) => {
    return await apiRequest(`/company/${companyId}/public-model-quotas`);
  },

  setPublicModelQuota: async (companyId, quotaData) => {
    return await apiRequest(`/company/${companyId}/public-model-quotas`, {
      method: 'PUT',
      body: JSON.stringify(quotaData)
    });
  },

  deletePublicModelQuota: async (companyId, quotaId) => {
    return await apiRequest(`/company/${companyId}/public-model-quotas/${quotaId}`, {
      method: 'DELETE'
    });
  },

  // =====================================================
  // Группы компании
  // =====================================================

  // Получение списка групп компании
  getGroups: async (companyId) => {
    return await apiRequest(`/company/${companyId}/groups`);
  },

  // Создание группы
  createGroup: async (companyId, groupData) => {
    return await apiRequest(`/company/${companyId}/groups`, {
      method: 'POST',
      body: JSON.stringify(groupData)
    });
  },

  // Обновление группы
  updateGroup: async (companyId, groupId, groupData) => {
    return await apiRequest(`/company/${companyId}/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData)
    });
  },

  // Удаление группы
  deleteGroup: async (companyId, groupId) => {
    return await apiRequest(`/company/${companyId}/groups/${groupId}`, {
      method: 'DELETE'
    });
  },

  // Обновление участников группы
  lookupCompanyUser: async (companyId, email) => {
    return await apiRequest(`/company/${companyId}/user-lookup?email=${encodeURIComponent(email)}`);
  },

  updateGroupMembers: async (companyId, groupId, userIds) => {
    return await apiRequest(`/company/${companyId}/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_ids: userIds })
    });
  },

  // Получение списка моделей, назначенных группе
  getGroupModels: async (companyId, groupId) => {
    return await apiRequest(`/company/${companyId}/groups/${groupId}/models`);
  },

  // Назначение группе публичных и локальных моделей (whitelist)
  setGroupModels: async (companyId, groupId, modelIds) => {
    return await apiRequest(`/company/${companyId}/groups/${groupId}/models`, {
      method: 'PUT',
      body: JSON.stringify({ model_ids: modelIds })
    });
  },

  // =====================================================
  // Права на приложения
  // =====================================================

  // Получение всех прав на приложения
  getAppPermissions: async (companyId) => {
    return await apiRequest(`/company/${companyId}/app-permissions`);
  },

  // Назначение права на приложение
  grantAppPermission: async (companyId, permData) => {
    return await apiRequest(`/company/${companyId}/app-permissions`, {
      method: 'POST',
      body: JSON.stringify(permData)
    });
  },

  // Массовое назначение прав
  bulkGrantAppPermissions: async (companyId, permData) => {
    return await apiRequest(`/company/${companyId}/app-permissions/bulk`, {
      method: 'POST',
      body: JSON.stringify(permData)
    });
  },

  // Отзыв права
  revokeAppPermission: async (companyId, permissionId) => {
    return await apiRequest(`/company/${companyId}/app-permissions/${permissionId}`, {
      method: 'DELETE'
    });
  },

  // Отзыв всех прав для группы или пользователя
  revokeAllPermissions: async (companyId, { groupId, userId }) => {
    const params = new URLSearchParams();
    if (groupId) params.append('group_id', groupId);
    if (userId) params.append('user_id', userId);
    return await apiRequest(`/company/${companyId}/app-permissions/by-target?${params.toString()}`, {
      method: 'DELETE'
    });
  },

  // Получение разрешённых приложений для текущего пользователя
  getAllowedApps: async () => {
    return await apiRequest('/company/my/allowed-apps');
  }
};

// API для Технического писателя - генерация документации по IT-схемам
export const techWriterAPI = {
  // Загрузка изображения схемы
  uploadImage: async (formData) => {
    try {
      return await uploadFileRequest('tech-writer/upload', formData);
    } catch (error) {
      console.error('Ошибка загрузки изображения:', error);
      throw error;
    }
  },

  // Получение списка задач
  getTasks: async (page = 1, pageSize = 20, statusFilter = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (statusFilter) {
      params.append('status_filter', statusFilter);
    }
    
    return await apiRequest(`/tech-writer/tasks?${params.toString()}`);
  },

  // Получение конкретной задачи
  getTask: async (taskId) => {
    return await apiRequest(`/tech-writer/tasks/${taskId}`);
  },

  // Удаление задачи
  deleteTask: async (taskId) => {
    return await apiRequest(`/tech-writer/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  // Получение статистики
  getStats: async () => {
    return await apiRequest('/tech-writer/stats');
  },

  // Скачивание DOCX документа
  downloadDocx: async (taskId) => {
    const downloadWithToken = async (token = null) => {
      const headers = token 
        ? { 'Authorization': `Bearer ${token}` }
        : getAuthHeaders();
        
      const response = await fetch(`/api/tech-writer/tasks/${taskId}/download-docx`, {
        method: 'GET',
        headers: headers
      });
      
      if (response.status === 401 && !token) {
        console.log('🔐 Токен истек, обновляем...');
        const newToken = await refreshAuthToken();
        if (newToken) {
          return await downloadWithToken(newToken);
        }
        throw new Error('Сессия истекла, требуется повторная авторизация');
      }
      
      if (!response.ok) {
        let errorMsg = 'Ошибка скачивания документа';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          errorMsg = `${errorMsg} (${response.status})`;
        }
        throw new Error(errorMsg);
      }
      
      return response;
    };
    
    try {
      const response = await downloadWithToken();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `TechDoc_${taskId}.docx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Ошибка скачивания DOCX:', error);
      throw error;
    }
  }
};

// API для AI Презентаций
export const aiPresentationAPI = {
  // Генерация новой презентации
  generate: async (data) => {
    const documents = Array.isArray(data?.documents) ? data.documents.filter(Boolean) : [];
    if (documents.length > 0) {
      const formData = new FormData();
      Object.entries(data || {}).forEach(([key, value]) => {
        if (key === 'documents') return;
        if (value === undefined || value === null || value === '') return;
        formData.append(key, typeof value === 'boolean' ? String(value) : value);
      });
      documents.forEach((file) => {
        formData.append('documents', file);
      });

      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/ai-presentation/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Ошибка генерации презентации');
      }

      return response.json();
    }

    return await apiRequest('/ai-presentation/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Новый конструктор Reveal.js
  createRevealPresentation: async (data) => {
    return await apiRequest('/ai-presentation/reveal', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  planRevealAgent: async (data) => {
    return await apiRequest('/ai-presentation/reveal/agent/plan', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  applyRevealAgent: async (data) => {
    return await apiRequest('/ai-presentation/reveal/agent/apply', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Загрузка шаблона презентации (PPTX)
  uploadTemplate: async (file, name = '', description = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);

    const token = localStorage.getItem('accessToken');
    const response = await fetch('/api/ai-presentation/user/templates', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Ошибка загрузки шаблона');
    }

    return response.json();
  },

  // Список шаблонов пользователя
  getTemplates: async (page = 1, perPage = 20) => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });
    return await apiRequest(`/ai-presentation/user/templates?${params.toString()}`);
  },

  // Получение одного шаблона
  getTemplate: async (templateId) => {
    return await apiRequest(`/ai-presentation/user/templates/${templateId}`);
  },

  // Обновление шаблона
  updateTemplate: async (templateId, data) => {
    return await apiRequest(`/ai-presentation/user/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  createRevealTemplate: async (data) => {
    return await apiRequest('/ai-presentation/user/templates/reveal', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Статус обработки шаблона
  getTemplateStatus: async (templateId) => {
    return await apiRequest(`/ai-presentation/user/templates/${templateId}/status`);
  },

  // Установка шаблона по умолчанию
  setDefaultTemplate: async (templateId) => {
    return await apiRequest(`/ai-presentation/user/templates/${templateId}/set-default`, {
      method: 'POST'
    });
  },

  // Удаление шаблона
  deleteTemplate: async (templateId) => {
    return await apiRequest(`/ai-presentation/user/templates/${templateId}`, {
      method: 'DELETE'
    });
  },

  // Получение статуса генерации
  getStatus: async (presentationId) => {
    return await apiRequest(`/ai-presentation/status/${presentationId}`);
  },

  // Получение презентации
  getPresentation: async (presentationId) => {
    return await apiRequest(`/ai-presentation/${presentationId}`);
  },

  // Получение списка презентаций
  getPresentations: async (page = 1, perPage = 10, status = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });
    
    if (status) {
      params.append('status', status);
    }
    
    return await apiRequest(`/ai-presentation/?${params.toString()}`);
  },

  // Обновление презентации
  updatePresentation: async (presentationId, data) => {
    return await apiRequest(`/ai-presentation/${presentationId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Удаление презентации
  deletePresentation: async (presentationId) => {
    return await apiRequest(`/ai-presentation/${presentationId}`, {
      method: 'DELETE'
    });
  },

  // Улучшение текста с помощью AI
  refineText: async (data) => {
    return await apiRequest('/ai-presentation/refine-text', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Перегенерация изображения
  regenerateImage: async (data) => {
    return await apiRequest('/ai-presentation/regenerate-image', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Получение статистики
  getStats: async () => {
    return await apiRequest('/ai-presentation/user/stats');
  },

  // Получение списка тем
  getThemes: async () => {
    return await apiRequest('/ai-presentation/themes/list');
  },

  // Экспорт в PPTX
  exportToPptx: async (presentationId) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ai-presentation/${presentationId}/export/pptx`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Ошибка экспорта презентации');
    }
    
    return response.blob();
  },

  exportToPdf: async (presentationId) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/ai-presentation/${presentationId}/export/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Ошибка экспорта презентации в PDF');
    }
    
    return response.blob();
  }
};

// API для тикетной системы поддержки
export const supportAPI = {
  // Получение списка тикетов пользователя
  getTickets: async (page = 1, pageSize = 10, status = null) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (status) {
      params.append('status', status);
    }
    
    return await apiRequest(`/support/tickets?${params.toString()}`);
  },

  // Получение детальной информации о тикете
  getTicketDetails: async (ticketId) => {
    return await apiRequest(`/support/tickets/${ticketId}`);
  },

  // Создание нового тикета
  createTicket: async (ticketData) => {
    return await apiRequest('/support/tickets', {
      method: 'POST',
      body: JSON.stringify(ticketData)
    });
  },

  // Добавление сообщения в тикет
  addMessage: async (ticketId, messageData) => {
    return await apiRequest(`/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  },

  // Получение статистики по тикетам пользователя
  getStats: async () => {
    return await apiRequest('/support/tickets/stats');
  },

  // Методы для администраторов
  
  // Получение списка всех тикетов (администратор)
  getAdminTickets: async (page = 1, pageSize = 10, filters = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString()
    });
    
    if (filters.status) {
      params.append('status', filters.status);
    }
    
    if (filters.priority) {
      params.append('priority', filters.priority);
    }
    
    if (filters.assigned_to) {
      params.append('assigned_to', filters.assigned_to);
    }
    
    if (filters.user_id) {
      params.append('user_id', filters.user_id);
    }
    
    return await apiRequest(`/support/admin/tickets?${params.toString()}`);
  },

  // Обновление тикета (администратор)
  updateTicket: async (ticketId, updateData) => {
    return await apiRequest(`/support/admin/tickets/${ticketId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  // Решение тикета (администратор)
  resolveTicket: async (ticketId, resolutionData) => {
    return await apiRequest(`/support/admin/tickets/${ticketId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(resolutionData)
    });
  },

  // Получение статистики по тикетам (администратор)
  getAdminStats: async () => {
    return await apiRequest('/support/admin/tickets/stats');
  }
};

// ==================== NOTIFICATION API ====================
export const notificationAPI = {
  // Получение статистики уведомлений
  getStats: async () => {
    try {
      return await throttledGetRequest(
        'notifications/stats',
        60000,
        { quietErrors: true, timeout: 10000 }
      );
    } catch (error) {
      if ([502, 503, 504].includes(error?.status)) {
        return { service_unavailable: true };
      }
      throw error;
    }
  },

  // Получение списка уведомлений
  getNotifications: async (skip = 0, limit = 20, includeRead = true) => {
    const params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
      include_read: includeRead.toString()
    });
    return await apiRequest(`notifications?${params.toString()}`);
  },

  // Получение конкретного уведомления
  getNotification: async (notificationId) => {
    return await apiRequest(`notifications/${notificationId}`);
  },

  // Обновление уведомления (пометка прочитанным)
  updateNotification: async (notificationId, updateData) => {
    return await apiRequest(`notifications/${notificationId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  },

  // Пометить все уведомления как прочитанные
  markAllRead: async () => {
    return await apiRequest('notifications/mark-all-read', {
      method: 'POST'
    });
  },

  // Удаление уведомления
  deleteNotification: async (notificationId) => {
    return await apiRequest(`notifications/${notificationId}`, {
      method: 'DELETE'
    });
  },

  // Удаление всех уведомлений
  deleteAllNotifications: async () => {
    return await apiRequest('notifications/delete-all', {
      method: 'DELETE'
    });
  }
};

// ==================== CAPTCHA API ====================
export const captchaAPI = {
  // Получить challenge для капчи
  getChallenge: async () => {
    return await apiRequest('/altcha/challenge');
  },

  // Проверить результат капчи
  verify: async (payload) => {
    return await apiRequest('/altcha/verify', {
      method: 'POST',
      body: JSON.stringify({ payload })
    });
  }
};

// ==================== AI POLYGON API ====================
export const aiPolygonAPI = {
  // Создание диаграммы
  createDiagram: async (data) => {
    return await apiRequest('/ai-polygon/diagrams', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // Получение списка диаграмм
  getDiagrams: async (page = 1, perPage = 12) => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });
    return await apiRequest(`/ai-polygon/diagrams?${params.toString()}`);
  },

  // Получение диаграммы по ID
  getDiagram: async (diagramId) => {
    return await apiRequest(`/ai-polygon/diagrams/${diagramId}`);
  },

  // Обновление диаграммы
  updateDiagram: async (diagramId, data) => {
    return await apiRequest(`/ai-polygon/diagrams/${diagramId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  // Удаление диаграммы
  deleteDiagram: async (diagramId) => {
    return await apiRequest(`/ai-polygon/diagrams/${diagramId}`, {
      method: 'DELETE'
    });
  },

  // Отправка сообщения ассистенту с историей чата для контекста
  sendToAssistant: async (diagramId, message, action = 'explain', sceneData = null, chatHistory = null) => {
    return await apiRequest(`/ai-polygon/diagrams/${diagramId}/assistant`, {
      method: 'POST',
      body: JSON.stringify({ 
        message, 
        action, 
        scene_data: sceneData,
        chat_history: chatHistory  // История чата для контекстного понимания
      })
    });
  },

  // Генерация диаграммы из промпта
  generateDiagram: async (message, templateType = 'blank') => {
    return await apiRequest('/ai-polygon/generate', {
      method: 'POST',
      body: JSON.stringify({ message, scene_data: { template_type: templateType } })
    });
  },

  // Получение шаблонов
  getTemplates: async () => {
    return await apiRequest('/ai-polygon/templates');
  },

  // Получение статистики
  getStats: async () => {
    return await apiRequest('/ai-polygon/user/stats');
  },

  // Получение сообщений диаграммы
  getMessages: async (diagramId) => {
    return await apiRequest(`/ai-polygon/diagrams/${diagramId}/messages`);
  }
};

export const biSystemAPI = {
  getStatus: async () => {
    return await apiRequest('/bi-system/status');
  },

  getProjects: async () => {
    return await apiRequest('/bi-system/projects');
  },

  getDefaultProject: async () => {
    return await apiRequest('/bi-system/projects/default');
  },

  createProject: async (data) => {
    return await apiRequest('/bi-system/projects', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  deleteProject: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}`, {
      method: 'DELETE'
    });
  },

  getDataSources: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}/data-sources`);
  },

  deleteDataSource: async (sourceId) => {
    return await apiRequest(`/bi-system/data-sources/${sourceId}`, {
      method: 'DELETE'
    });
  },

  previewPostgresSource: async (data) => {
    return await apiRequest('/bi-system/data-sources/postgresql/preview', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  importPostgresTable: async (data) => {
    return await apiRequest('/bi-system/data-sources/postgresql/import', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  uploadXlsx: async (projectId, file) => {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`/api/bi-system/data-sources/xlsx/upload?project_id=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data?.detail || `HTTP ${response.status}`);
    }

    return await response.json();
  },

  getDatasets: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}/datasets`);
  },

  getDataset: async (datasetId) => {
    return await apiRequest(`/bi-system/datasets/${datasetId}`);
  },

  getDatasetPreview: async (datasetId, limit = 50) => {
    return await apiRequest(`/bi-system/datasets/${datasetId}/preview?limit=${limit}`);
  },

  syncDatasetToCube: async (datasetId) => {
    return await apiRequest(`/bi-system/datasets/${datasetId}/sync-cube`, {
      method: 'POST'
    });
  },

  getLocalModels: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}/chat/models`);
  },

  getChatConversations: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}/chat/conversations`);
  },

  createChatConversation: async (projectId, data = {}) => {
    return await apiRequest(`/bi-system/projects/${projectId}/chat/conversations`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getChatConversation: async (conversationId) => {
    return await apiRequest(`/bi-system/chat/conversations/${conversationId}`);
  },

  deleteChatConversation: async (conversationId) => {
    return await apiRequest(`/bi-system/chat/conversations/${conversationId}`, {
      method: 'DELETE'
    });
  },

  sendChatMessage: async (conversationId, data) => {
    return await longRunningApiRequest(`/bi-system/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getDashboards: async (projectId) => {
    return await apiRequest(`/bi-system/projects/${projectId}/dashboards`);
  },

  createDashboard: async (projectId, data) => {
    return await apiRequest(`/bi-system/projects/${projectId}/dashboards`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getDashboard: async (dashboardId) => {
    return await apiRequest(`/bi-system/dashboards/${dashboardId}`);
  },

  updateDashboard: async (dashboardId, data) => {
    return await apiRequest(`/bi-system/dashboards/${dashboardId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteDashboard: async (dashboardId) => {
    return await apiRequest(`/bi-system/dashboards/${dashboardId}`, {
      method: 'DELETE'
    });
  },

  createWidget: async (dashboardId, data) => {
    return await apiRequest(`/bi-system/dashboards/${dashboardId}/widgets`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  updateWidget: async (widgetId, data) => {
    return await apiRequest(`/bi-system/widgets/${widgetId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  deleteWidget: async (widgetId) => {
    return await apiRequest(`/bi-system/widgets/${widgetId}`, {
      method: 'DELETE'
    });
  }
};

// ==================== VIDZHEY API ====================
export const vidzheyAPI = {
  getRooms: async ({ archived = false } = {}) => {
    const query = archived ? '?archived=true' : '';
    return await apiRequest(`/vidzhey/rooms${query}`);
  },

  createRoom: async (data) => {
    return await apiRequest('/vidzhey/rooms', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getRoom: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}`);
  },

  updateRoom: async (roomId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  closeRoom: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}`, {
      method: 'DELETE'
    });
  },

  archiveRoom: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/archive`, {
      method: 'POST'
    });
  },

  unarchiveRoom: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/unarchive`, {
      method: 'POST'
    });
  },

  deleteRoom: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/permanent`, {
      method: 'DELETE'
    });
  },

  getJoinToken: async (roomId, { resetChat = false } = {}) => {
    const params = resetChat ? '?reset_chat=true' : '';
    return await apiRequest(`/vidzhey/rooms/${roomId}/join-token${params}`, {
      method: 'POST'
    });
  },

  getAgentState: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/agent`);
  },

  getChatMessages: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat`, { quietErrors: true });
  },

  getChatHistory: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat/history`, { quietErrors: true });
  },

  addChatMessage: async (roomId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  uploadChatAttachment: async (roomId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat/attachments`, {
      method: 'POST',
      body: formData,
      quietErrors: true
    });
  },

  deleteChatMessage: async (roomId, messageId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat/${messageId}`, {
      method: 'DELETE',
      quietErrors: true
    });
  },

  updateChatReaction: async (roomId, messageId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/chat/${messageId}/reaction`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendParticipantCommand: async (roomId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/participant-command`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendParticipantReaction: async (roomId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/participant-reaction`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendParticipantHand: async (roomId, data) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/participant-hand`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  getPublicChatMessages: async (slug, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/chat${query}`, { quietErrors: true });
  },

  addPublicChatMessage: async (slug, data, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/chat${query}`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  uploadPublicChatAttachment: async (slug, file, participantToken, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    const formData = new FormData();
    formData.append('file', file);
    formData.append('participant_token', participantToken);
    return await apiRequest(`/vidzhey/public/${slug}/chat/attachments${query}`, {
      method: 'POST',
      body: formData,
      quietErrors: true
    });
  },

  deletePublicChatMessage: async (slug, messageId, participantToken, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/chat/${messageId}${query}`, {
      method: 'DELETE',
      body: JSON.stringify({ participant_token: participantToken }),
      quietErrors: true
    });
  },

  updatePublicChatReaction: async (slug, messageId, data, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/chat/${messageId}/reaction${query}`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendPublicParticipantReaction: async (slug, data, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/participant-reaction${query}`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendPublicParticipantHand: async (slug, data, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/participant-hand${query}`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  sendPublicParticipantCommand: async (slug, data) => {
    return await apiRequest(`/vidzhey/public/${slug}/participant-command`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  startAgent: async (roomId, data = {}) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/agent/start`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  autoStartAgent: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/agent/auto-start`, {
      method: 'POST',
      quietErrors: true
    });
  },

  autoStartPublicAgent: async (slug, accessCode = '') => {
    const params = new URLSearchParams();
    if (accessCode) params.set('access_code', accessCode);
    const query = params.toString() ? `?${params.toString()}` : '';
    return await apiRequest(`/vidzhey/public/${slug}/agent/auto-start${query}`, {
      method: 'POST',
      quietErrors: true
    });
  },

  stopAgent: async (roomId, reason = '') => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/agent/stop`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  },

  restartAgent: async (roomId, data = {}) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/agent/restart`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getPublicRoom: async (slug) => {
    return await apiRequest(`/vidzhey/public/${slug}`, { quietErrors: true });
  },

  closePublicRoom: async (slug, participantToken) => {
    return await apiRequest(`/vidzhey/public/${slug}/close`, {
      method: 'POST',
      body: JSON.stringify({ participant_token: participantToken })
    });
  },

  joinPublicRoom: async (slug, data) => {
    return await apiRequest(`/vidzhey/public/${slug}/join`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  joinPublicRoomAsUser: async (slug, data) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-auth`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  createJoinRequest: async (slug, data) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-request`, {
      method: 'POST',
      body: JSON.stringify(data),
      quietErrors: true
    });
  },

  getJoinRequestStatus: async (slug, requestId) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-request/${requestId}`, {
      quietErrors: true
    });
  },

  cancelJoinRequest: async (slug, requestId) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-request/${requestId}`, {
      method: 'DELETE',
      quietErrors: true
    });
  },

  approveJoinRequest: async (roomId, requestId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/join-requests/${requestId}/approve`, {
      method: 'POST',
      quietErrors: true
    });
  },

  rejectJoinRequest: async (roomId, requestId, reason = null) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/join-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      quietErrors: true
    });
  },

  approvePublicJoinRequest: async (slug, requestId, participantToken, reason = null) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-requests/${requestId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ participant_token: participantToken, reason }),
      quietErrors: true
    });
  },

  rejectPublicJoinRequest: async (slug, requestId, participantToken, reason = null) => {
    return await apiRequest(`/vidzhey/public/${slug}/join-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ participant_token: participantToken, reason }),
      quietErrors: true
    });
  },

  getRecordings: async (roomId) => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/recordings`);
  },

  getMyRecordings: async () => {
    return await apiRequest(`/vidzhey/my-recordings`);
  },

  startRecording: async (roomId, downloadVisibility = 'private') => {
    return await apiRequest(`/vidzhey/rooms/${roomId}/recordings`, {
      method: 'POST',
      body: JSON.stringify({ download_visibility: downloadVisibility })
    });
  },

  stopRecording: async (recordingId) => {
    return await apiRequest(`/vidzhey/recordings/${recordingId}/stop`, {
      method: 'POST'
    });
  },

  transcribeRecording: async (recordingId) => {
    return await apiRequest(`/vidzhey/recordings/${recordingId}/transcribe`, {
      method: 'POST'
    });
  },

  updateRecording: async (recordingId, data) => {
    return await apiRequest(`/vidzhey/recordings/${recordingId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  downloadRecording: async (recordingId, fileName = 'vidzhey-recording.mp4') => {
    const response = await fetch(`/api/vidzhey/recordings/${recordingId}/download`, {
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Не удалось скачать запись');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

// ==================== SMART LINKS API ====================
export const smartLinksAPI = {
  getLinks: async (page = 1, pageSize = 10, tag = null) => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    if (tag !== null) {
      query.set('tag', tag);
    }
    return await apiRequest(`/smart-links/links?${query.toString()}`);
  },

  getTags: async () => {
    return await apiRequest('/smart-links/links/tags');
  },

  createLink: async (data) => {
    return await apiRequest('/smart-links/links', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getLink: async (linkId) => {
    return await apiRequest(`/smart-links/links/${linkId}`);
  },

  updateLink: async (linkId, data) => {
    return await apiRequest(`/smart-links/links/${linkId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  },

  deleteLink: async (linkId) => {
    return await apiRequest(`/smart-links/links/${linkId}`, {
      method: 'DELETE'
    });
  },

  updateQrSettings: async (linkId, qrSettings) => {
    return await apiRequest(`/smart-links/links/${linkId}/qr-settings`, {
      method: 'PATCH',
      body: JSON.stringify({ qr_settings: qrSettings })
    });
  },

  getStats: async (linkId, { period = 'month', startDate = '', endDate = '' } = {}) => {
    const query = new URLSearchParams({ period });
    if (period === 'custom' && startDate && endDate) {
      query.set('start_date', startDate);
      query.set('end_date', endDate);
    }
    return await apiRequest(`/smart-links/links/${linkId}/stats?${query.toString()}`);
  },

  exportStats: async (linkId, { period = 'month', startDate = '', endDate = '' } = {}) => {
    const query = new URLSearchParams({ period });
    if (period === 'custom' && startDate && endDate) {
      query.set('start_date', startDate);
      query.set('end_date', endDate);
    }

    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/smart-links/links/${linkId}/stats/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      let message = 'Не удалось выгрузить статистику';
      try {
        const data = await response.json();
        if (typeof data?.detail === 'string') {
          message = data.detail;
        }
      } catch (err) {
        const errorText = await response.text();
        if (errorText) message = errorText;
      }
      throw new Error(message);
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'smart-link-stats.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

// ==================== LEGAL AUDIT API (подраздел Умных ссылок) ====================
export const legalAuditAPI = {
  getAudits: async (page = 1, pageSize = 10) => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    return await apiRequest(`/smart-links/legal-audit/audits?${query.toString()}`);
  },

  createAudit: async (data) => {
    return await apiRequest('/smart-links/legal-audit/audits', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  getAudit: async (auditId) => {
    return await apiRequest(`/smart-links/legal-audit/audits/${auditId}`);
  },

  deleteAudit: async (auditId) => {
    return await apiRequest(`/smart-links/legal-audit/audits/${auditId}`, {
      method: 'DELETE'
    });
  },

  rerunAudit: async (auditId) => {
    return await apiRequest(`/smart-links/legal-audit/audits/${auditId}/rerun`, {
      method: 'POST'
    });
  },

  exportAudit: async (auditId, format = 'xlsx') => {
    const token = localStorage.getItem('accessToken');
    const query = new URLSearchParams({ format });
    const response = await fetch(`/api/smart-links/legal-audit/audits/${auditId}/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      const errorText = await response.text();
      const label = format === 'pdf' ? 'PDF-отчёт' : 'Excel-отчёт';
      throw new Error(errorText || `Не удалось скачать ${label}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `legal-audit-${auditId}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  screenshotUrl: (taskId, fileName) => {
    return `/api/smart-links/legal-audit/uploads/${taskId}/${encodeURIComponent(fileName)}`;
  },

  // Скриншоты защищены auth — <img src> не шлёт заголовок Authorization,
  // поэтому грузим картинку fetch'ем с токеном и возвращаем blob-URL.
  fetchScreenshot: async (taskId, fileName) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/smart-links/legal-audit/uploads/${taskId}/${encodeURIComponent(fileName)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
  }
};

// ==================== SITE ANALYZER API ====================
export const siteAnalyzerAPI = {
  getTasks: async (page = 1, pageSize = 10) => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    return await apiRequest(`/site-analyzer/tasks?${query.toString()}`);
  },

  createTask: async (data) => {
    return await apiRequest('/site-analyzer/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: 300000
    });
  },

  getTask: async (taskId) => {
    return await apiRequest(`/site-analyzer/tasks/${taskId}`);
  },

  getDashboard: async (taskId) => {
    return await apiRequest(`/site-analyzer/tasks/${taskId}/dashboard`);
  },

  rerunTask: async (taskId) => {
    return await apiRequest(`/site-analyzer/tasks/${taskId}/rerun`, {
      method: 'POST',
      timeout: 300000
    });
  },

  deleteTask: async (taskId) => {
    return await apiRequest(`/site-analyzer/tasks/${taskId}`, {
      method: 'DELETE'
    });
  },

  getPdfUrl: (taskId) => {
    const token = localStorage.getItem('accessToken');
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    const query = params.toString();
    return `/api/site-analyzer/tasks/${taskId}/pdf${query ? `?${query}` : ''}`;
  },

  downloadPdf: async (taskId) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`/api/site-analyzer/tasks/${taskId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Не удалось скачать PDF-отчёт');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `site-analyzer-${taskId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};


// ==================== APP BUILDER API ====================
export const appBuilderAPI = {
  // --- Приложения ---
  listApps: async (includeCompany = true) => {
    const params = new URLSearchParams({ include_company: String(includeCompany) });
    return await apiRequest(`/app-builder/apps?${params.toString()}`);
  },

  getApp: async (appId) => {
    return await apiRequest(`/app-builder/apps/${appId}`);
  },

  createApp: async (data) => {
    return await apiRequest('/app-builder/apps', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateApp: async (appId, data) => {
    return await apiRequest(`/app-builder/apps/${appId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteApp: async (appId) => {
    return await apiRequest(`/app-builder/apps/${appId}`, { method: 'DELETE' });
  },

  publishApp: async (appId) => {
    return await apiRequest(`/app-builder/apps/${appId}/publish`, { method: 'POST' });
  },

  unpublishApp: async (appId) => {
    return await apiRequest(`/app-builder/apps/${appId}/unpublish`, { method: 'POST' });
  },

  // --- Версии ---
  listVersions: async (appId) => {
    return await apiRequest(`/app-builder/apps/${appId}/versions`);
  },

  saveVersion: async (appId, graph) => {
    return await apiRequest(`/app-builder/apps/${appId}/versions`, {
      method: 'POST',
      body: JSON.stringify({ graph }),
    });
  },

  activateVersion: async (appId, versionId) => {
    return await apiRequest(`/app-builder/apps/${appId}/versions/${versionId}/activate`, {
      method: 'POST',
    });
  },

  // --- Запуски ---
  createRun: async (appId, inputs, conversationId = null) => {
    const body = { inputs: inputs || {} };
    if (conversationId) body.conversation_id = conversationId;
    return await apiRequest(`/app-builder/apps/${appId}/runs`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  getRun: async (runId) => {
    return await apiRequest(`/app-builder/runs/${runId}`);
  },

  listRuns: async (appId, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return await apiRequest(`/app-builder/apps/${appId}/runs?${params.toString()}`);
  },

  // --- Сообщения чата ---
  listConversationMessages: async (conversationId) => {
    return await apiRequest(`/app-builder/conversations/${conversationId}/messages`);
  },

  postChatMessage: async (runId, content) => {
    return await apiRequest(`/app-builder/runs/${runId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },

  // --- Загрузка файлов ---
  uploadFile: async (appId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return await apiRequest(`/app-builder/apps/${appId}/upload`, {
      method: 'POST',
      body: formData,
    });
  },

  // --- Доступные LLM-модели ---
  listLLMModels: async () => {
    return await apiRequest('/app-builder/llm-models');
  },

  // --- Папки базы знаний (RAG v2) ---
  listRAGFolders: async () => {
    return await apiRequest('/rag-v2/folders?include_all=true');
  },
};

const downloadAuthenticatedBlob = async (url, { method = 'GET', body, filename, headers: extraHeaders } = {}) => {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  Object.assign(headers, extraHeaders || {});
  if (body) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const errorText = await response.text();
    let message = errorText || 'Не удалось скачать файл';
    try {
      const parsed = JSON.parse(errorText);
      if (typeof parsed.detail === 'string') {
        message = parsed.detail;
      }
    } catch (error) {
      message = errorText || 'Не удалось скачать файл';
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const osvAnalysisAPI = {
  getJobs: async (page = 1, pageSize = 10) => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    return await apiRequest(`/osv-analysis/jobs?${query.toString()}`);
  },

  createJob: async ({ files, threshold, title, periods }) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('threshold', String(threshold ?? 0));
    if (title) {
      formData.append('title', title);
    }
    if (periods) {
      formData.append('periods', JSON.stringify(periods));
    }
    return await uploadFileRequest('osv-analysis/jobs', formData);
  },

  getJob: async (jobId) => {
    return await apiRequest(`/osv-analysis/jobs/${jobId}`);
  },

  getDashboard: async (jobId) => {
    return await apiRequest(`/osv-analysis/jobs/${jobId}/dashboard`);
  },

  updateJob: async (jobId, data) => {
    return await apiRequest(`/osv-analysis/jobs/${jobId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteJob: async (jobId) => {
    return await apiRequest(`/osv-analysis/jobs/${jobId}`, {
      method: 'DELETE',
    });
  },

  downloadReport: async (jobId) => {
    await downloadAuthenticatedBlob(`/api/osv-analysis/jobs/${jobId}/export`, {
      filename: `analiz-osv-${jobId}.xlsx`,
    });
  },

  downloadFiltered: async (jobId, payload) => {
    await downloadAuthenticatedBlob(`/api/osv-analysis/jobs/${jobId}/export-filtered`, {
      method: 'POST',
      body: payload,
      filename: `osv-dashboard-${payload.report_id || 'filter'}.xlsx`,
    });
  },
};

export const zaoModuleAPI = {
  getReports: async (page = 1, pageSize = 10, status = '') => {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    if (status) query.set('status', status);
    return await apiRequest(`/zao-module/reports?${query.toString()}`);
  },

  createReport: async (payload) => {
    const formData = new FormData();
    payload.files.forEach((file) => formData.append('files', file));
    formData.append('last_name', payload.lastName);
    formData.append('initials', payload.initials);
    if (payload.department) formData.append('department', payload.department);
    if (payload.position) formData.append('position', payload.position);
    if (payload.personnelNumber) formData.append('personnel_number', payload.personnelNumber);
    if (payload.advancePurpose) formData.append('advance_purpose', payload.advancePurpose);
    if (payload.advanceAmount) formData.append('advance_amount', payload.advanceAmount);
    return await uploadFileRequest('zao-module/reports', formData, 1, 14);
  },

  getReport: async (reportId) => apiRequest(`/zao-module/reports/${reportId}`),

  retryReport: async (reportId) => apiRequest(`/zao-module/reports/${reportId}/retry`, { method: 'POST' }),

  deleteReport: async (reportId) => apiRequest(`/zao-module/reports/${reportId}`, { method: 'DELETE' }),

  downloadReport: async (reportId, filename) => {
    await downloadAuthenticatedBlob(`/api/zao-module/reports/${reportId}/export`, {
      filename: filename || `advance-report-${reportId}.xlsx`,
    });
  },
};

const diskQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const text = query.toString();
  return text ? `?${text}` : '';
};

export const diskAPI = {
  getState: async () => apiRequest('disk/state'),
  listNodes: async ({ section = 'files', parentId, q, sort = 'name' } = {}) => (
    apiRequest(`disk/nodes${diskQuery({ section, parent_id: parentId, q, sort })}`)
  ),
  createFolder: async (name, parentId) => apiRequest('disk/folders', {
    method: 'POST',
    body: JSON.stringify({ name, parent_id: parentId || null }),
  }),
  upload: async (file, parentId, relativePath) => {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId) formData.append('parent_id', parentId);
    if (relativePath) formData.append('relative_path', relativePath);
    return uploadFileRequest('disk/upload', formData);
  },
  rename: async (nodeId, name) => apiRequest(`disk/nodes/${nodeId}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  }),
  trash: async (nodeId) => apiRequest(`disk/nodes/${nodeId}/trash`, { method: 'POST' }),
  restore: async (nodeId) => apiRequest(`disk/nodes/${nodeId}/restore`, { method: 'POST' }),
  remove: async (nodeId) => apiRequest(`disk/nodes/${nodeId}`, { method: 'DELETE' }),
  download: async (nodeId, filename) => downloadAuthenticatedBlob(`/api/disk/nodes/${nodeId}/download`, { filename }),
  share: async (nodeId, pin, audience = 'registered') => apiRequest(`disk/nodes/${nodeId}/share`, {
    method: 'POST',
    body: JSON.stringify({ pin, audience }),
  }),
  unshare: async (nodeId) => apiRequest(`disk/nodes/${nodeId}/share`, { method: 'DELETE' }),
  listShared: async () => apiRequest('disk/shared'),
  openShared: async (shareId, pin, parentId) => apiRequest(`disk/shared/${shareId}/open`, {
    method: 'POST',
    body: JSON.stringify({ pin, parent_id: parentId || null }),
  }),
  downloadShared: async (shareId, pin, nodeId, filename) => downloadAuthenticatedBlob(
    `/api/disk/shared/${shareId}/download${diskQuery({ node_id: nodeId })}`,
    { filename, headers: { 'X-Disk-Pin': pin } }
  ),
  linkMeta: async (token) => apiRequest(`disk/links/${token}`),
  openLink: async (token, pin, parentId) => apiRequest(`disk/links/${token}/open`, {
    method: 'POST',
    body: JSON.stringify({ pin, parent_id: parentId || null }),
  }),
  downloadLink: async (token, pin, nodeId, filename) => downloadAuthenticatedBlob(
    `/api/disk/links/${token}/download${diskQuery({ node_id: nodeId })}`,
    { filename, headers: { 'X-Disk-Pin': pin } }
  ),
  getUserBucket: async () => apiRequest('disk/user-bucket'),
  saveUserBucket: async (data) => apiRequest('disk/user-bucket', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteUserBucket: async () => apiRequest('disk/user-bucket', { method: 'DELETE' }),
  testUserBucket: async (data) => apiRequest('disk/user-bucket/test', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  listRemote: async (prefix = '') => apiRequest(`disk/user-bucket/objects${diskQuery({ prefix })}`),
  listS3Keys: async () => apiRequest('disk/s3-keys'),
  createS3Key: async (name) => apiRequest('disk/s3-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  }),
  deleteS3Key: async (keyId) => apiRequest(`disk/s3-keys/${keyId}`, { method: 'DELETE' }),
  purchaseStorage: async (gigabytes) => apiRequest('disk/storage/purchase', {
    method: 'POST',
    body: JSON.stringify({ gigabytes }),
  }),
};

export const diskAdminAPI = {
  overview: async () => apiRequest('admin/disk/overview'),
  createBucket: async (data) => apiRequest('admin/disk/buckets', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateBucket: async (bucketId, data) => apiRequest(`admin/disk/buckets/${bucketId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteBucket: async (bucketId) => apiRequest(`admin/disk/buckets/${bucketId}`, { method: 'DELETE' }),
  testBucket: async (data) => apiRequest('admin/disk/buckets/test', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  sync: async () => apiRequest('admin/disk/sync', { method: 'POST' }),
  quotas: async (q = '') => apiRequest(`admin/disk/quotas${diskQuery({ q })}`),
  setQuota: async (userId, quotaMb) => apiRequest(`admin/disk/quotas/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ quota_mb: quotaMb }),
  }),
  clearQuota: async (userId) => apiRequest(`admin/disk/quotas/${userId}`, { method: 'DELETE' }),
  savePricing: async (data) => apiRequest('admin/disk/pricing', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};
